import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axiosLib from "axios";
import { fireAdGenerated } from "@/lib/pixel";
import { useAuth } from "@/context/AuthContext";

// Create axios instance with credentials for cookie-based auth
const axios = axiosLib.create({
  withCredentials: true,
  timeout: 600000 // 10 minutes timeout for long-running video generation
});

// Attach token from localStorage for Safari/iOS support
const TOKEN_KEY = 'prysh_session_token';
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  ArrowLeft, 
  ArrowRight, 
  Check, 
  Link as LinkIcon, 
  Sparkles, 
  Upload,
  Play,
  Download,
  Loader2,
  Image as ImageIcon,
  Video,
  FileText,
  Monitor,
  Smartphone,
  Mic,
  Pencil,
  Film,
  Lock,
  User,
  Package,
  Wand2,
  AlertTriangle,
  X,
  RefreshCw,
  Plus,
  Clock,
  CheckCircle,
  Clapperboard
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Steps for 20 Second Ad flow
const STEPS_20S_AD = [
  { id: 1, label: "Video Type", icon: Film },
  { id: 2, label: "Product URL", icon: LinkIcon },
  { id: 3, label: "Concepts", icon: Sparkles },
  { id: 4, label: "Images", icon: Upload },
  { id: 5, label: "Script", icon: FileText },
  { id: 6, label: "Anchor Images", icon: ImageIcon },
  { id: 7, label: "Videos", icon: Video },
  { id: 8, label: "Audio", icon: Mic },
  { id: 9, label: "Final", icon: Download },
];

// Steps for 10 Second Ad flow (same structure as 20S but 2 scenes)
const STEPS_10S_AD = [
  { id: 1, label: "Video Type", icon: Film },
  { id: 2, label: "Product URL", icon: LinkIcon },
  { id: 3, label: "Concepts", icon: Sparkles },
  { id: 4, label: "Images", icon: Upload },
  { id: 5, label: "Script", icon: FileText },
  { id: 6, label: "Anchor Images", icon: ImageIcon },
  { id: 7, label: "Videos", icon: Video },
  { id: 8, label: "Audio", icon: Mic },
  { id: 9, label: "Final", icon: Download },
];

// Steps for 20 Second Ad V2 flow (same as 20S AD)
const STEPS_20S_AD_V2 = [
  { id: 1, label: "Video Type", icon: Film },
  { id: 2, label: "Product URL", icon: LinkIcon },
  { id: 3, label: "Concepts", icon: Sparkles },
  { id: 4, label: "Images", icon: Upload },
  { id: 5, label: "Script", icon: FileText },
  { id: 6, label: "Anchor Images", icon: ImageIcon },
  { id: 7, label: "Videos", icon: Video },
  { id: 8, label: "Audio", icon: Mic },
  { id: 9, label: "Final", icon: Download },
];

// Steps for 20 Second Ad V3 flow (simplified - most steps are automated)
const STEPS_20S_AD_V3 = [
  { id: 1, label: "Video Type", icon: Film },
  { id: 2, label: "Product URL", icon: LinkIcon },
  { id: 3, label: "Audio", icon: Mic },
  { id: 4, label: "Final", icon: Download },
];

// Steps for 10 Second Ad Auto V5 flow (fully automated like V3 but 10s)
const STEPS_10S_AD_V5 = [
  { id: 1, label: "Video Type", icon: Film },
  { id: 2, label: "Product URL", icon: LinkIcon },
  { id: 3, label: "Audio", icon: Mic },
  { id: 4, label: "Final", icon: Download },
];

// Steps for UGC Video flow
const STEPS_UGC = [
  { id: 1, label: "Video Type", icon: Film },
  { id: 2, label: "Product URL", icon: LinkIcon },
  { id: 3, label: "Avatar", icon: User },
  { id: 4, label: "Product", icon: Package },
  { id: 5, label: "UGC Images", icon: ImageIcon },
  { id: 6, label: "Script", icon: FileText },
  { id: 7, label: "Generate", icon: Wand2 },
  { id: 8, label: "Final", icon: Download },
];

const STEPS_UGC_MULTI = [
  { id: 1, label: "Video Type", icon: Film },
  { id: 2, label: "Product URL", icon: LinkIcon },
  { id: 3, label: "Avatar", icon: User },
  { id: 4, label: "Product", icon: Package },
  { id: 5, label: "Generate Images", icon: ImageIcon },
  { id: 6, label: "Select Images", icon: CheckCircle },
  { id: 7, label: "Scripts", icon: FileText },
  { id: 8, label: "Generate Videos", icon: Wand2 },
  { id: 9, label: "Final", icon: Download },
];

const STEPS_UGC_SORA2_STORY = [
  { id: 1, label: "Video Type", icon: Film },
  { id: 2, label: "Product URL", icon: LinkIcon },
  { id: 3, label: "Avatar", icon: User },
  { id: 4, label: "Product", icon: Package },
  { id: 5, label: "Generate Images", icon: ImageIcon },
  { id: 6, label: "Select Image", icon: CheckCircle },
  { id: 7, label: "Scripts", icon: FileText },
  { id: 8, label: "Generate Video", icon: Wand2 },
  { id: 9, label: "Final", icon: Download },
];

const STEPS_UGC_BROLL = [
  { id: 1, label: "Video Type", icon: Film },
  { id: 2, label: "Product URL", icon: LinkIcon },
  { id: 3, label: "Avatar", icon: User },
  { id: 4, label: "Product", icon: Package },
  { id: 5, label: "Generate Images", icon: ImageIcon },
  { id: 6, label: "Select Images", icon: CheckCircle },
  { id: 7, label: "Scripts", icon: FileText },
  { id: 8, label: "Generate Videos", icon: Wand2 },
  { id: 9, label: "B-Roll", icon: Clapperboard },
  { id: 10, label: "Final", icon: Download },
];

const VOICE_TONES = [
  "Friendly American casual",
  "Enthusiastic American female",
  "Calm British male",
  "Energetic Australian",
  "Warm Indian English",
  "Professional neutral",
  "Excited Gen-Z casual",
  "Soothing ASMR whisper",
];

const ProjectWizard = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { refreshCredits } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [highestStep, setHighestStep] = useState(1);
  
  // Step-specific state
  const [videoType, setVideoType] = useState("ugc_multi_video"); // Video type selection
  const [productUrl, setProductUrl] = useState("");
  const [selectedLLM, setSelectedLLM] = useState("gemini"); // Always use Gemini
  const [selectedConcept, setSelectedConcept] = useState(null);
  const [aspectRatio, setAspectRatio] = useState("9:16");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState("21m00Tcm4TlvDq8ikWAM");
  const [uploadPreviews, setUploadPreviews] = useState([]);
  const [imageGenApproach, setImageGenApproach] = useState("gemini"); // Always use Gemini
  const [audioType, setAudioType] = useState("voiceover"); // "voiceover" or "music"
  const [musicMoods, setMusicMoods] = useState([]);
  const [selectedMood, setSelectedMood] = useState("upbeat");
  
  // Video editing state
  const [editingSceneIndex, setEditingSceneIndex] = useState(null);
  const [editPrompt, setEditPrompt] = useState("");
  const [regeneratingScene, setRegeneratingScene] = useState(false);
  const [videoVersions, setVideoVersions] = useState({});
  const [musicVersion, setMusicVersion] = useState(0);
  const [fullscreenImageIndex, setFullscreenImageIndex] = useState(null);
  const [regeneratingConcepts, setRegeneratingConcepts] = useState(false);
  const [showCustomConcept, setShowCustomConcept] = useState(false);
  const [customConcept, setCustomConcept] = useState({ title: "", hook: "", storyline: "", tone: "" });
  const [savingCustomConcept, setSavingCustomConcept] = useState(false);
  const [voiceoverVersion, setVoiceoverVersion] = useState(0);
  
  // Credit pricing
  const [creditPricing, setCreditPricing] = useState({});

  const CreditBadge = ({ actionKey, className = "" }) => {
    const cost = creditPricing[actionKey];
    if (!cost) return null;
    return (
      <span className={`inline-flex items-center gap-1 text-xs text-amber-600 font-medium ${className}`} data-testid={`credit-badge-${actionKey}`}>
        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v12M6 12h12"/></svg>
        {cost} {cost === 1 ? "credit" : "credits"}
      </span>
    );
  };
  
  // Image editing state
  const [editingImageIndex, setEditingImageIndex] = useState(null);
  const [imageEditPrompt, setImageEditPrompt] = useState("");
  const [regeneratingImage, setRegeneratingImage] = useState(false);
  const [imageVersions, setImageVersions] = useState({});
  
  // QC (Quality Control) state
  const [qcFailedScenes, setQcFailedScenes] = useState([]);
  const [showQcDialog, setShowQcDialog] = useState(false);
  const [retryingQc, setRetryingQc] = useState(false);
  const [qcType, setQcType] = useState("image"); // "image" or "video"
  
  // Background job progress state
  const [jobProgress, setJobProgress] = useState(null); // { progress: 0-100, message: "..." }
  
  // Editable product analysis state
  const [editableAnalysis, setEditableAnalysis] = useState(null);
  const [analysisEdited, setAnalysisEdited] = useState(false);
  const [savingAnalysis, setSavingAnalysis] = useState(false);
  
  // Step 2 input mode: "url" or "manual"
  const [step2Mode, setStep2Mode] = useState("url");
  const [manualProductName, setManualProductName] = useState("");
  const [manualProductDesc, setManualProductDesc] = useState("");
  
  // UGC-specific state
  const [ugcAvatarFile, setUgcAvatarFile] = useState(null);
  const [ugcAvatarPreview, setUgcAvatarPreview] = useState(null);
  const [ugcAvatarMode, setUgcAvatarMode] = useState("existing"); // "existing" or "upload"
  const [selectedDefaultAvatar, setSelectedDefaultAvatar] = useState(null);
  const [studioAvatars, setStudioAvatars] = useState([]); // From GET /api/avatars (presets + custom)
  const [avatarRegionFilter, setAvatarRegionFilter] = useState("all"); // "all" | "american" | "indian"
  const [studioAvatarsLoading, setStudioAvatarsLoading] = useState(false);

  // Per-scene / per-clip retry state (UGC Multi Video + B-Roll)
  const [videoEditCost, setVideoEditCost] = useState(2); // credits for video_edit action; refreshed from GET /credits/pricing
  const [retryOpenKey, setRetryOpenKey] = useState(null); // e.g. "scene-0" or "broll-2" — controls which prompt textarea is open
  const [retryPromptDraft, setRetryPromptDraft] = useState(""); // editable prompt for the currently-open retry
  const [retryingKey, setRetryingKey] = useState(null); // active retry key while BG job is in flight
  const [ugcProductFile, setUgcProductFile] = useState(null);
  const [ugcProductPreview, setUgcProductPreview] = useState(null);
  const [ugcGeneratedImages, setUgcGeneratedImages] = useState([]);
  const [ugcSelectedImageIndex, setUgcSelectedImageIndex] = useState(null);
  const [ugcScript, setUgcScript] = useState(null);
  const [editableUgcScript, setEditableUgcScript] = useState("");

  // UGC Multi-Scene state
  const [ugcMultiImages, setUgcMultiImages] = useState([]);
  const [ugcMultiSelected, setUgcMultiSelected] = useState([]);
  const [ugcMultiScripts, setUgcMultiScripts] = useState(["", "", "", ""]);
  const [voiceTone, setVoiceTone] = useState("Friendly American casual");
  const [subtitleStyle, setSubtitleStyle] = useState("bold_pop");
  const [brollClips, setBrollClips] = useState([]);
  const [brollPrompts, setBrollPrompts] = useState([]);

  const isUgcType = videoType === "ugc_video" || videoType === "ugc_sora2_video" || videoType === "ugc_multi_video" || videoType === "ugc_broll_video" || videoType === "ugc_sora2_story";
  const isUgcSingleType = videoType === "ugc_video" || videoType === "ugc_sora2_video";
  const isUgcStoryType = videoType === "ugc_sora2_story";

  // Fetch Avatar Studio library (presets + user's custom avatars) when UGC Step 3 becomes active
  useEffect(() => {
    if (!isUgcType || currentStep !== 3) return;
    if (studioAvatars.length > 0 || studioAvatarsLoading) return;
    let cancelled = false;
    (async () => {
      try {
        setStudioAvatarsLoading(true);
        const res = await axios.get(`${API}/avatars`, { withCredentials: true });
        if (!cancelled) {
          const data = res.data || {};
          const merged = Array.isArray(data)
            ? data
            : [...(data.custom || []), ...(data.presets || [])];
          setStudioAvatars(merged);
        }
      } catch (err) {
        console.error("Failed to load Avatar Studio library:", err);
        if (!cancelled) toast.error("Failed to load avatar library");
      } finally {
        if (!cancelled) setStudioAvatarsLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // studioAvatarsLoading intentionally omitted to avoid Strict-Mode cleanup race
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUgcType, currentStep, studioAvatars.length]);

  // Get the appropriate steps based on video type
  const STEPS = videoType === "ugc_video" || videoType === "ugc_sora2_video" ? STEPS_UGC : 
                videoType === "ugc_multi_video" ? STEPS_UGC_MULTI :
                videoType === "ugc_sora2_story" ? STEPS_UGC_SORA2_STORY :
                videoType === "ugc_broll_video" ? STEPS_UGC_BROLL :
                videoType === "10_second_ad" ? STEPS_10S_AD :
                videoType === "10_second_ad_v5" ? STEPS_10S_AD_V5 :
                videoType === "20_second_ad_v2" ? STEPS_20S_AD_V2 :
                videoType === "20_second_ad_v3" ? STEPS_20S_AD_V3 :
                videoType === "24_second_ad_premium" ? STEPS_20S_AD : STEPS_20S_AD;

  // Keep highestStep in sync when currentStep advances
  useEffect(() => {
    setHighestStep(prev => Math.max(prev, currentStep));
  }, [currentStep]);

  // Persist currentStep to DB whenever it changes (skip initial mount)
  const initialLoadDone = useRef(false);
  useEffect(() => {
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      return;
    }
    if (!projectId || currentStep <= 1) return;
    axios.patch(`${API}/projects/${projectId}`, { current_step: currentStep });
  }, [currentStep, projectId]);

  // Fetch credit pricing once
  useEffect(() => {
    axios.get(`${API.replace('/api', '')}/api/credits/pricing`)
      .then(res => {
        const map = {};
        (res.data.pricing || []).forEach(p => { map[p.action_key] = p.credits_cost; });
        setCreditPricing(map);
      })
      .catch(() => {});
  }, []);


  const fetchProject = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`);
      const proj = response.data.project;
      setProject(proj);
      setCurrentStep(proj.current_step || 1);
      setHighestStep(prev => Math.max(prev, proj.current_step || 1));
      setProductUrl(proj.product_url || "");
      setSelectedConcept(proj.selected_concept_id || null);
      setAspectRatio(proj.aspect_ratio || "9:16");
      if (proj.video_type) setVideoType(proj.video_type);
      if (proj.llm) setSelectedLLM(proj.llm);
      if (proj.generation_approach) setImageGenApproach(proj.generation_approach);
      if (proj.audio_type) setAudioType(proj.audio_type);
      if (proj.music_mood) setSelectedMood(proj.music_mood);
      
      // Load UGC-specific state
      if (proj.ugc_avatar_image) setUgcAvatarPreview(`${API}/files/${proj.ugc_avatar_image.split('/').pop()}`);
      if (proj.ugc_product_image) setUgcProductPreview(`${API}/files/${proj.ugc_product_image.split('/').pop()}`);
      if (proj.ugc_generated_images) setUgcGeneratedImages(proj.ugc_generated_images);
      if (proj.ugc_selected_image_index !== undefined) setUgcSelectedImageIndex(proj.ugc_selected_image_index);
      if (proj.ugc_script) {
        setUgcScript(proj.ugc_script);
        setEditableUgcScript(proj.ugc_script.voiceover_text || "");
      }
      
      // Load UGC Multi-Scene state
      if (proj.ugc_multi_images) setUgcMultiImages(proj.ugc_multi_images);
      if (proj.ugc_multi_selected) setUgcMultiSelected(proj.ugc_multi_selected.map(img => img.index));
      if (proj.ugc_multi_scripts) setUgcMultiScripts(proj.ugc_multi_scripts);
      if (proj.ugc_multi_voice_tone) setVoiceTone(proj.ugc_multi_voice_tone);
      
      // Populate editable analysis if exists
      if (proj.product_analysis) {
        setEditableAnalysis({
          product_name: proj.product_analysis.product_name || "",
          category: proj.product_analysis.category || "",
          price_range: proj.product_analysis.price_range || "",
          key_features: proj.product_analysis.key_features || [],
          emotional_hooks: proj.product_analysis.emotional_hooks || [],
          primary_use_case: proj.product_analysis.primary_use_case || "",
          target_audience: proj.product_analysis.target_audience || "",
          objections_to_handle: proj.product_analysis.objections_to_handle || [],
        });
      }
    } catch (error) {
      console.error("Error fetching project:", error);
      toast.error("Failed to load project");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  // Fetch available voices
  useEffect(() => {
    const fetchVoices = async () => {
      try {
        const response = await axios.get(`${API}/voices`);
        if (response.data.voices) {
          setVoices(response.data.voices);
        }
      } catch (error) {
        console.error("Error fetching voices:", error);
      }
    };
    fetchVoices();
  }, []);

  // Fetch credit pricing once so we can render actual cost on retry buttons
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await axios.get(`${API}/credits/pricing`, { withCredentials: true });
        const pricing = res.data?.pricing || res.data || [];
        const videoEdit = Array.isArray(pricing)
          ? pricing.find(p => p.action_key === "video_edit")
          : null;
        if (videoEdit?.credits_cost != null) {
          setVideoEditCost(Number(videoEdit.credits_cost));
        }
      } catch (e) {
        // silently fall back to the default value
      }
    };
    fetchPricing();
  }, []);

  // Fetch music moods
  useEffect(() => {
    const fetchMoods = async () => {
      try {
        const response = await axios.get(`${API}/music-moods`);
        if (response.data.moods) {
          setMusicMoods(response.data.moods);
        }
      } catch (error) {
        console.error("Error fetching music moods:", error);
      }
    };
    fetchMoods();
  }, []);

  // Step 1: Select Video Type
  const selectVideoType = async () => {
    setProcessing(true);
    try {
      await axios.patch(`${API}/projects/${projectId}`, { 
        video_type: videoType,
        current_step: 2
      });
      refreshCredits();
      await fetchProject();
      setCurrentStep(2);
    } catch (error) {
      console.error("Error saving video type:", error);
      toast.error(error.response?.data?.detail || "Failed to save video type");
    } finally {
      setProcessing(false);
    }
  };

  // Step 2: Analyze Product
  const analyzeProduct = async () => {
    if (!productUrl.trim()) {
      toast.error("Please enter a product URL");
      return;
    }

    setProcessing(true);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/analyze`, { 
        product_url: productUrl,
        llm: selectedLLM
      });
      toast.success("Product analyzed! Review and edit the details below.");
      
      // Populate editable analysis from response
      if (res.data.analysis) {
        setEditableAnalysis({
          product_name: res.data.analysis.product_name || "",
          category: res.data.analysis.category || "",
          price_range: res.data.analysis.price_range || "",
          key_features: res.data.analysis.key_features || [],
          emotional_hooks: res.data.analysis.emotional_hooks || [],
          primary_use_case: res.data.analysis.primary_use_case || "",
          target_audience: res.data.analysis.target_audience || "",
          objections_to_handle: res.data.analysis.objections_to_handle || [],
        });
      }
      
      await fetchProject();
      // Don't auto-advance — let user review/edit analysis first
    } catch (error) {
      console.error("Error analyzing product:", error);
      toast.error("Failed to analyze product");
    } finally {
      setProcessing(false);
    }
  };

  // Step 2 V3: Analyze Product with auto image scraping and single concept
  const analyzeProductV3 = async () => {
    if (!productUrl.trim()) {
      toast.error("Please enter a product URL");
      return;
    }

    setProcessing(true);
    try {
      toast.info("Analyzing product and scraping images...");
      
      // Only analyze product — don't run the full pipeline yet
      const res = await axios.post(`${API}/projects/${projectId}/analyze-v3`, { 
        product_url: productUrl,
        llm: selectedLLM
      });
      toast.success("Product analyzed! Review and edit the details below.");
      
      if (res.data.analysis) {
        setEditableAnalysis({
          product_name: res.data.analysis.product_name || "",
          category: res.data.analysis.category || "",
          price_range: res.data.analysis.price_range || "",
          key_features: res.data.analysis.key_features || [],
          emotional_hooks: res.data.analysis.emotional_hooks || [],
          primary_use_case: res.data.analysis.primary_use_case || "",
          target_audience: res.data.analysis.target_audience || "",
          objections_to_handle: res.data.analysis.objections_to_handle || [],
        });
      }
      
      await fetchProject();
      // Don't auto-advance — let user review/edit analysis first
    } catch (error) {
      console.error("Error in V3 analysis:", error);
      toast.error(error.response?.data?.detail || "Failed to process product");
    } finally {
      setProcessing(false);
    }
  };

  // V5 Auto Flow: 10 Second Ad Fully Automated
  const analyzeProductV5 = async () => {
    if (!productUrl.trim()) {
      toast.error("Please enter a product URL");
      return;
    }

    setProcessing(true);
    try {
      toast.info("Analyzing product and scraping images...");
      
      // Only analyze product — don't run the full pipeline yet
      const res = await axios.post(`${API}/projects/${projectId}/analyze-v5`, { 
        product_url: productUrl,
        llm: selectedLLM
      });
      toast.success("Product analyzed! Review and edit the details below.");
      
      if (res.data.analysis) {
        setEditableAnalysis({
          product_name: res.data.analysis.product_name || "",
          category: res.data.analysis.category || "",
          price_range: res.data.analysis.price_range || "",
          key_features: res.data.analysis.key_features || [],
          emotional_hooks: res.data.analysis.emotional_hooks || [],
          primary_use_case: res.data.analysis.primary_use_case || "",
          target_audience: res.data.analysis.target_audience || "",
          objections_to_handle: res.data.analysis.objections_to_handle || [],
        });
      }
      
      await fetchProject();
      // Don't auto-advance — let user review/edit analysis first
    } catch (error) {
      console.error("Error in V5 analysis:", error);
      toast.error(error.response?.data?.detail || "Failed to process product");
    } finally {
      setProcessing(false);
    }
  };

  // Step 3: Select Concept
  const selectConcept = async () => {
    if (!selectedConcept) {
      toast.error("Please select a concept");
      return;
    }

    setProcessing(true);
    try {
      await axios.post(`${API}/projects/${projectId}/select-concept`, {
        concept_id: selectedConcept,
        aspect_ratio: aspectRatio
      });
      toast.success("Concept selected!");
      await fetchProject();
      setCurrentStep(4);
    } catch (error) {
      console.error("Error selecting concept:", error);
      toast.error("Failed to select concept");
    } finally {
      setProcessing(false);
    }
  };

  // Generate new concepts (replace existing ones)
  const regenerateConcepts = async () => {
    setRegeneratingConcepts(true);
    try {
      toast.info("Generating new concepts...");
      const response = await axios.post(`${API}/projects/${projectId}/concepts`);
      setSelectedConcept(null);
      setProject(prev => ({ ...prev, concepts: response.data.concepts }));
      toast.success("New concepts generated!");
    } catch (error) {
      console.error("Error regenerating concepts:", error);
      toast.error("Failed to generate new concepts");
    } finally {
      setRegeneratingConcepts(false);
    }
  };

  // Save user's custom concept and auto-select it
  const saveCustomConcept = async () => {
    if (!customConcept.title.trim() || !customConcept.hook.trim() || !customConcept.tone.trim()) {
      toast.error("Please fill in Title, Hook, and Tone");
      return;
    }
    setSavingCustomConcept(true);
    try {
      const response = await axios.post(`${API}/projects/${projectId}/custom-concept`, customConcept);
      setSelectedConcept("custom_1");
      setProject(prev => ({
        ...prev,
        concepts: [...(prev.concepts || []).filter(c => c.id !== "custom_1"), response.data.concept],
        selected_concept_id: "custom_1"
      }));
      setShowCustomConcept(false);
      toast.success("Custom concept saved! Click Continue to proceed.");
    } catch (error) {
      console.error("Error saving custom concept:", error);
      toast.error(error.response?.data?.detail || "Failed to save concept");
    } finally {
      setSavingCustomConcept(false);
    }
  };

  // Step 4: Upload Images
  const SUPPORTED_IMAGE_FORMATS = ['png', 'jpg', 'jpeg', 'webp', 'gif'];
  
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files).slice(0, 4);
    
    // Validate file formats
    const unsupportedFiles = files.filter(file => {
      const ext = file.name.split('.').pop()?.toLowerCase() || '';
      return !SUPPORTED_IMAGE_FORMATS.includes(ext);
    });
    
    if (unsupportedFiles.length > 0) {
      const unsupportedNames = unsupportedFiles.map(f => f.name).join(', ');
      toast.error(`Unsupported format: ${unsupportedNames}. Please use PNG, JPEG, WEBP, or GIF.`);
      return;
    }
    
    setUploadedFiles(files);
    const previews = files.map(file => URL.createObjectURL(file));
    setUploadPreviews(previews);
  };

  const uploadImages = async () => {
    if (uploadedFiles.length === 0) {
      toast.error("Please upload at least one product image");
      return;
    }

    setProcessing(true);
    try {
      const formData = new FormData();
      uploadedFiles.forEach(file => formData.append('files', file));
      
      await axios.post(`${API}/projects/${projectId}/upload-images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Images uploaded!");
      
      // Generate script
      await axios.post(`${API}/projects/${projectId}/script`);
      toast.success("Script generated!");
      
      await fetchProject();
      setCurrentStep(5);
    } catch (error) {
      console.error("Error uploading images:", error);
      // Show specific error message from backend if available
      const errorMessage = error.response?.data?.detail || "Failed to upload images";
      toast.error(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  // Generic polling helper for background jobs
  const pollJobStatus = async (jobType, onComplete, onFail, maxAttempts = 120) => {
    let attempts = 0;
    setJobProgress({ progress: 5, message: "Starting..." });
    while (attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 5000));
      try {
        const res = await axios.get(
          `${API}/projects/${projectId}/job-status/${jobType}?t=${Date.now()}`,
          { headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate', 'Pragma': 'no-cache' } }
        );
        const job = res.data;
        if (job.status === "completed") {
          setJobProgress({ progress: 100, message: job.message || "Done!" });
          if (jobType === "stitch_video") fireAdGenerated();
          await onComplete(job);
          setJobProgress(null);
          return;
        } else if (job.status === "failed") {
          setJobProgress(null);
          throw new Error(job.error || "Job failed");
        } else {
          setJobProgress({ progress: job.progress || 10, message: job.message || "Processing..." });
        }
      } catch (pollError) {
        if (pollError.message && !pollError.message.includes("Network Error")) {
          setJobProgress(null);
          throw pollError;
        }
        console.error(`Error polling ${jobType}:`, pollError);
      }
      attempts++;
    }
    setJobProgress(null);
    throw new Error("Operation timed out. Please check project status.");
  };

  // Step 5: Generate Anchor Prompts & Images with QC (background job)
  const generateAnchorImages = async () => {
    setProcessing(true);
    try {
      await axios.post(`${API}/projects/${projectId}/anchor-prompts`);
      toast.success("Anchor prompts created!");
      
      toast.info("Generating anchor images... This may take a moment.");
      const startResponse = await axios.post(`${API}/projects/${projectId}/generate-anchor-images`, {
        approach: imageGenApproach
      });
      
      if (!startResponse.data.job_id) {
        throw new Error("Failed to start anchor image generation");
      }
      
      toast.info("Anchor image generation in progress. Please wait...");
      
      await pollJobStatus("anchor_image_generation", async (job) => {
        const { qc_failed_scenes, qc_summary } = job.result || {};
        if (qc_summary) {
          if (qc_summary.failed > 0) {
            toast.warning(`QC: ${qc_summary.passed}/${qc_summary.total} images passed. ${qc_summary.failed} need attention.`);
            setQcFailedScenes(qc_failed_scenes || []);
            setQcType("image");
            setShowQcDialog(true);
          } else {
            toast.success(`All ${qc_summary.total} anchor images passed QC!`);
          }
        } else {
          toast.success("Anchor images generated!");
        }
        await fetchProject();
        setCurrentStep(videoType === "20_second_ad_v3" ? 5 : 6);
      });
    } catch (error) {
      console.error("Error generating anchor images:", error);
      toast.error(error.message || "Failed to generate anchor images");
    } finally {
      setProcessing(false);
    }
  };

  // QC Retry handler for anchor images
  const retryQcForScene = async (sceneIndex) => {
    setRetryingQc(true);
    try {
      toast.info(`Regenerating and running QC for Scene ${sceneIndex + 1}...`);
      const response = await axios.post(`${API}/projects/${projectId}/retry-anchor-qc`, {
        scene_index: sceneIndex
      });
      
      const { qc_status, attempts, max_retries } = response.data;
      
      if (qc_status === "PASS") {
        toast.success(`Scene ${sceneIndex + 1} passed QC!`);
        // Remove from failed list
        setQcFailedScenes(prev => prev.filter(s => s.scene !== sceneIndex + 1));
      } else if (qc_status === "MANUAL_REVIEW") {
        toast.warning(`Scene ${sceneIndex + 1} flagged for manual review after ${max_retries} attempts.`);
        // Update failed list with manual review status
        setQcFailedScenes(prev => prev.map(s => 
          s.scene === sceneIndex + 1 ? { ...s, status: "MANUAL_REVIEW" } : s
        ));
      } else {
        toast.error(`Scene ${sceneIndex + 1} failed QC again (attempt ${attempts}/${max_retries})`);
      }
      
      const currentStepBackup = currentStep;
      await fetchProject();
      setCurrentStep(currentStepBackup);
      
      // Close dialog if all passed
      const remainingFailed = qcFailedScenes.filter(s => s.scene !== sceneIndex + 1);
      if (remainingFailed.length === 0 || qc_status === "PASS") {
        const updatedFailed = qcFailedScenes.filter(s => s.scene !== sceneIndex + 1);
        if (updatedFailed.length === 0) {
          setShowQcDialog(false);
        }
      }
    } catch (error) {
      console.error("Error retrying QC:", error);
      toast.error("Failed to retry QC");
    } finally {
      setRetryingQc(false);
    }
  };

  // Step 6: Generate Video Prompts & Videos (with background job polling)
  const generateVideos = async () => {
    setProcessing(true);
    try {
      // Generate video prompts
      await axios.post(`${API}/projects/${projectId}/video-prompts`);
      toast.success("Video prompts created!");
      
      // Start video generation job
      toast.info("Starting video generation... This may take several minutes.");
      const startResponse = await axios.post(`${API}/projects/${projectId}/generate-videos`);
      
      if (!startResponse.data.job_id) {
        throw new Error("Failed to start video generation job");
      }
      
      toast.info("Video generation in progress. Please wait...");
      
      // Poll using the video-specific endpoint but with progress bar
      let attempts = 0;
      const maxAttempts = 120;
      setJobProgress({ progress: 5, message: "Starting video generation..." });
      
      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 5000));
        
        try {
          const statusResponse = await axios.get(
            `${API}/projects/${projectId}/video-job-status?t=${Date.now()}`,
            { headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate', 'Pragma': 'no-cache' } }
          );
          const job = statusResponse.data;
          
          if (job.status === "completed") {
            setJobProgress({ progress: 100, message: "Videos generated!" });
            const { qc_failed_scenes, qc_summary } = job.result || {};
            
            if (qc_summary) {
              if (qc_summary.failed > 0) {
                toast.warning(`Video QC: ${qc_summary.passed}/${qc_summary.total} videos passed. ${qc_summary.failed} need attention.`);
                setQcFailedScenes(qc_failed_scenes || []);
                setQcType("video");
                setShowQcDialog(true);
              } else {
                toast.success(`All ${qc_summary.total} video scenes passed QC!`);
              }
            } else {
              toast.success("Videos generated!");
            }
            
            setJobProgress(null);
            await fetchProject();
            setCurrentStep(videoType === "20_second_ad_v3" ? 6 : 7);
            return;
          } else if (job.status === "failed") {
            setJobProgress(null);
            throw new Error(job.error || "Video generation failed");
          } else {
            setJobProgress({ progress: job.progress || 10, message: job.message || "Generating videos..." });
          }
        } catch (pollError) {
          if (pollError.message && !pollError.message.includes("Network Error")) {
            setJobProgress(null);
            throw pollError;
          }
          console.error("Error polling job status:", pollError);
        }
        
        attempts++;
      }
      
      setJobProgress(null);
      toast.error("Video generation timed out. Please check project status.");
      await fetchProject();
      
    } catch (error) {
      console.error("Error generating videos:", error);
      toast.error(error.message || "Failed to generate videos");
    } finally {
      setProcessing(false);
    }
  };

  // QC Retry handler for video scenes
  const retryVideoQcForScene = async (sceneIndex) => {
    setRetryingQc(true);
    try {
      toast.info(`Regenerating and running QC for Video Scene ${sceneIndex + 1}...`);
      const response = await axios.post(`${API}/projects/${projectId}/retry-video-qc`, {
        scene_index: sceneIndex
      });
      
      const { qc_status, attempts, max_retries } = response.data;
      
      if (qc_status === "PASS") {
        toast.success(`Video Scene ${sceneIndex + 1} passed QC!`);
        setQcFailedScenes(prev => prev.filter(s => s.scene !== sceneIndex + 1));
      } else if (qc_status === "MANUAL_REVIEW") {
        toast.warning(`Video Scene ${sceneIndex + 1} flagged for manual review after ${max_retries} attempts.`);
        setQcFailedScenes(prev => prev.map(s => 
          s.scene === sceneIndex + 1 ? { ...s, status: "MANUAL_REVIEW" } : s
        ));
      } else {
        toast.error(`Video Scene ${sceneIndex + 1} failed QC again (attempt ${attempts}/${max_retries})`);
      }
      
      const currentStepBackup = currentStep;
      await fetchProject();
      setCurrentStep(currentStepBackup);
      
      // Close dialog if all passed
      const updatedFailed = qcFailedScenes.filter(s => s.scene !== sceneIndex + 1);
      if (updatedFailed.length === 0 && qc_status === "PASS") {
        setShowQcDialog(false);
      }
    } catch (error) {
      console.error("Error retrying Video QC:", error);
      toast.error("Failed to retry Video QC");
    } finally {
      setRetryingQc(false);
    }
  };

  // Video Scene Editing
  const openEditDialog = (sceneIndex) => {
    const scene = project?.video_scenes?.[sceneIndex];
    if (scene) {
      setEditingSceneIndex(sceneIndex);
      setEditPrompt(videoType === "24_second_ad_premium" ? (scene.veo_prompt || scene.kling_prompt || "") : (scene.kling_prompt || ""));
    }
  };

  const closeEditDialog = () => {
    setEditingSceneIndex(null);
    setEditPrompt("");
  };

  const regenerateVideoScene = async () => {
    if (editingSceneIndex === null || !editPrompt.trim()) return;
    
    setRegeneratingScene(true);
    try {
      toast.info(`Regenerating Scene ${editingSceneIndex + 1}... This may take a minute.`);
      
      const startResponse = await axios.post(`${API}/projects/${projectId}/regenerate-video-scene`, {
        scene_index: editingSceneIndex,
        new_prompt: editPrompt
      });
      
      if (!startResponse.data.job_id) {
        throw new Error("Failed to start video regeneration");
      }
      
      toast.info("Video regeneration in progress. Please wait...");
      
      await pollJobStatus("regenerate_video_scene", async () => {
        toast.success(`Scene ${editingSceneIndex + 1} regenerated successfully!`);
        refreshCredits();
        setVideoVersions(prev => ({
          ...prev,
          [editingSceneIndex]: Date.now()
        }));
        const currentStepBackup = currentStep;
        await fetchProject();
        setCurrentStep(currentStepBackup);
        closeEditDialog();
      });
    } catch (error) {
      console.error("Error regenerating video scene:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Failed to regenerate video";
      toast.error(errorMessage);
    } finally {
      setRegeneratingScene(false);
    }
  };

  // ── UGC Multi Video (per-scene) + B-Roll (per-clip) retry ────────────────
  // Open the inline prompt editor for a given target ("scene-{i}" or "broll-{i}")
  const openRetryEditor = (key, currentPrompt) => {
    setRetryOpenKey(key);
    setRetryPromptDraft(currentPrompt || "");
  };
  const closeRetryEditor = () => {
    setRetryOpenKey(null);
    setRetryPromptDraft("");
  };

  const retryUgcMultiScene = async (sceneIndex) => {
    const draft = retryPromptDraft.trim();
    if (!draft) { toast.error("Prompt cannot be empty"); return; }
    if (project?.final_video_url) { toast.error("Final video already stitched. Cannot retry."); return; }
    const key = `scene-${sceneIndex}`;
    setRetryingKey(key);
    try {
      const startRes = await axios.post(`${API}/projects/${projectId}/regenerate-video-scene`, {
        scene_index: sceneIndex, new_prompt: draft
      }, { withCredentials: true });
      if (!startRes.data.job_id) throw new Error("Failed to start scene regeneration");
      setJobProgress({ progress: 5, message: `Retrying scene ${sceneIndex + 1}...` });
      await pollJobStatus("regenerate_video_scene", async () => {
        toast.success(`Scene ${sceneIndex + 1} regenerated (${videoEditCost} CR)`);
        refreshCredits();
        const stepBackup = currentStep;
        await fetchProject();
        setCurrentStep(stepBackup);
        closeRetryEditor();
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Failed to regenerate scene");
    } finally {
      setRetryingKey(null);
      setJobProgress(null);
    }
  };

  const retryBrollClip = async (clipIndex) => {
    const draft = retryPromptDraft.trim();
    if (!draft) { toast.error("Prompt cannot be empty"); return; }
    if (project?.final_video_url) { toast.error("Final video already stitched. Cannot retry."); return; }
    const key = `broll-${clipIndex}`;
    setRetryingKey(key);
    try {
      const startRes = await axios.post(`${API}/projects/${projectId}/ugc-broll/regenerate-clip`, {
        clip_index: clipIndex, new_prompt: draft
      }, { withCredentials: true });
      if (!startRes.data.job_id) throw new Error("Failed to start clip regeneration");
      setJobProgress({ progress: 5, message: `Retrying B-roll clip ${clipIndex + 1}...` });
      await pollJobStatus("regenerate_broll_clip", async () => {
        toast.success(`B-roll clip ${clipIndex + 1} regenerated (${videoEditCost} CR)`);
        refreshCredits();
        const stepBackup = currentStep;
        await fetchProject();
        setCurrentStep(stepBackup);
        closeRetryEditor();
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Failed to regenerate clip");
    } finally {
      setRetryingKey(null);
      setJobProgress(null);
    }
  };

  // Anchor Image Editing
  const openImageEditDialog = (sceneIndex) => {
    setEditingImageIndex(sceneIndex);
    setImageEditPrompt("");
  };

  const closeImageEditDialog = () => {
    setEditingImageIndex(null);
    setImageEditPrompt("");
  };

  const regenerateAnchorImage = async () => {
    if (editingImageIndex === null || !imageEditPrompt.trim()) return;
    
    setRegeneratingImage(true);
    try {
      toast.info(`Editing Scene ${editingImageIndex + 1} image... This may take a moment.`);
      
      const startResponse = await axios.post(`${API}/projects/${projectId}/regenerate-anchor-image`, {
        scene_index: editingImageIndex,
        edit_prompt: imageEditPrompt
      });
      
      if (!startResponse.data.job_id) {
        throw new Error("Failed to start image edit");
      }
      
      toast.info("Image edit in progress. Please wait...");
      
      await pollJobStatus("regenerate_anchor_image", async () => {
        toast.success(`Scene ${editingImageIndex + 1} image updated successfully!`);
        refreshCredits();
        setImageVersions(prev => ({
          ...prev,
          [editingImageIndex]: Date.now()
        }));
        const currentStepBackup = currentStep;
        await fetchProject();
        setCurrentStep(currentStepBackup);
        closeImageEditDialog();
      });
    } catch (error) {
      console.error("Error regenerating anchor image:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Failed to edit image";
      toast.error(errorMessage);
    } finally {
      setRegeneratingImage(false);
    }
  };

  // Step 8: Generate Voiceovers (background job)
  const generateVoiceovers = async () => {
    setProcessing(true);
    try {
      toast.info("Generating voiceovers for all scenes...");
      const startResponse = await axios.post(`${API}/projects/${projectId}/generate-voiceovers`, {
        voice_id: selectedVoice
      });
      
      if (!startResponse.data.job_id) {
        throw new Error("Failed to start voiceover generation");
      }
      
      toast.info("Voiceover generation in progress. Please wait...");
      
      await pollJobStatus("voiceover_generation", async () => {
        toast.success("Voiceovers generated!");
        refreshCredits();
        setVoiceoverVersion(Date.now());
        const currentStepBackup = currentStep;
        await fetchProject();
        setCurrentStep(currentStepBackup);
      });
    } catch (error) {
      console.error("Error generating voiceovers:", error);
      toast.error(error.message || "Failed to generate voiceovers");
    } finally {
      setProcessing(false);
    }
  };

  // Step 8: Generate Music (background job)
  const generateMusic = async () => {
    setProcessing(true);
    try {
      toast.info("Generating background music...");
      const startResponse = await axios.post(`${API}/projects/${projectId}/generate-music`, {
        mood: selectedMood
      });
      
      if (!startResponse.data.job_id) {
        throw new Error("Failed to start music generation");
      }
      
      toast.info("Music generation in progress. Please wait...");
      
      await pollJobStatus("music_generation", async () => {
        toast.success("Music generated!");
        refreshCredits();
        setMusicVersion(Date.now());
        const currentStepBackup = currentStep;
        await fetchProject();
        setCurrentStep(currentStepBackup);
      });
    } catch (error) {
      console.error("Error generating music:", error);
      toast.error(error.message || "Failed to generate music");
    } finally {
      setProcessing(false);
    }
  };

  // Step 8: Stitch Final Video (background job)
  const stitchVideo = async () => {
    setProcessing(true);
    try {
      toast.info("Starting video stitching...");
      const startResponse = await axios.post(`${API}/projects/${projectId}/stitch`, {
        audio_type: audioType
      });
      
      if (!startResponse.data.job_id) {
        throw new Error("Failed to start video stitching");
      }
      
      toast.info("Video stitching in progress. Please wait...");
      
      await pollJobStatus("stitch_video", async () => {
        toast.success("Final video created!");
        await fetchProject();
        const finalStep = (videoType === "20_second_ad_v3" || videoType === "10_second_ad_v5") ? 4 : (videoType === "ugc_video" || videoType === "ugc_sora2_video") ? 8 : 9;
        setCurrentStep(finalStep);
      });
    } catch (error) {
      console.error("Error stitching video:", error);
      toast.error(error.message || "Failed to create final video");
    } finally {
      setProcessing(false);
    }
  };

  // ============ UGC VIDEO HANDLERS ============

  // UGC Step 2: Analyze product for UGC (simpler - just save URL and get product info)
  const analyzeProductForUGC = async () => {
    if (!productUrl.trim()) {
      toast.error("Please enter a product URL");
      return;
    }

    setProcessing(true);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/analyze`, { 
        product_url: productUrl,
        llm: selectedLLM
      });
      toast.success("Product analyzed! Review and edit the details below.");
      
      if (res.data.analysis) {
        setEditableAnalysis({
          product_name: res.data.analysis.product_name || "",
          category: res.data.analysis.category || "",
          price_range: res.data.analysis.price_range || "",
          key_features: res.data.analysis.key_features || [],
          emotional_hooks: res.data.analysis.emotional_hooks || [],
          primary_use_case: res.data.analysis.primary_use_case || "",
          target_audience: res.data.analysis.target_audience || "",
          objections_to_handle: res.data.analysis.objections_to_handle || [],
        });
      }
      
      await fetchProject();
      // Don't auto-advance — let user review/edit analysis first
    } catch (error) {
      console.error("Error analyzing product:", error);
      toast.error("Failed to analyze product");
    } finally {
      setProcessing(false);
    }
  };

  // Analyze product from manual input (name + description)
  const analyzeProductManual = async () => {
    if (!manualProductName.trim() || !manualProductDesc.trim()) {
      toast.error("Please enter both product name and description");
      return;
    }

    setProcessing(true);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/analyze-manual`, {
        product_name: manualProductName.trim(),
        product_description: manualProductDesc.trim(),
        llm: selectedLLM
      });
      toast.success("Product analyzed! Review and edit the details below.");

      if (res.data.analysis) {
        setEditableAnalysis({
          product_name: res.data.analysis.product_name || "",
          category: res.data.analysis.category || "",
          price_range: res.data.analysis.price_range || "",
          key_features: res.data.analysis.key_features || [],
          emotional_hooks: res.data.analysis.emotional_hooks || [],
          primary_use_case: res.data.analysis.primary_use_case || "",
          target_audience: res.data.analysis.target_audience || "",
          objections_to_handle: res.data.analysis.objections_to_handle || [],
        });
      }

      await fetchProject();
    } catch (error) {
      console.error("Error in manual analysis:", error);
      toast.error(error.response?.data?.detail || "Failed to analyze product");
    } finally {
      setProcessing(false);
    }
  };

  // Save edited product analysis and continue to next step
  const saveAnalysisAndContinue = async () => {
    if (!editableAnalysis) return;
    
    // Clean up array fields (remove empty entries)
    const cleanedAnalysis = {
      ...editableAnalysis,
      key_features: (editableAnalysis.key_features || []).map(s => s.trim()).filter(Boolean),
      emotional_hooks: (editableAnalysis.emotional_hooks || []).map(s => s.trim()).filter(Boolean),
      objections_to_handle: (editableAnalysis.objections_to_handle || []).map(s => s.trim()).filter(Boolean),
    };
    
    setSavingAnalysis(true);
    setProcessing(true);
    try {
      // Save the edited analysis
      await axios.put(`${API}/projects/${projectId}/product-analysis`, cleanedAnalysis);
      setAnalysisEdited(false);
      
      // Continue pipeline based on video type
      if (videoType === "ugc_video" || videoType === "ugc_sora2_video" || videoType === "ugc_multi_video" || videoType === "ugc_broll_video") {
        // UGC: just advance to avatar step
        toast.success("Analysis saved!");
        await fetchProject();
        setCurrentStep(3);
      } else if (videoType === "20_second_ad_v3") {
        // V3 pipeline: concepts → script → anchors → videos
        toast.info("Generating concept...");
        await axios.post(`${API}/projects/${projectId}/concepts-v3`);
        toast.success("Concept generated!");
        
        toast.info("Generating script...");
        await axios.post(`${API}/projects/${projectId}/auto-select-and-script`);
        toast.success("Script generated!");
        
        toast.info("Creating anchor image prompts...");
        await axios.post(`${API}/projects/${projectId}/anchor-prompts`);
        toast.success("Anchor prompts created!");
        
        toast.info("Generating anchor images...");
        await axios.post(`${API}/projects/${projectId}/generate-anchor-images`, { approach: "gemini" });
        toast.success("Anchor images generated!");
        
        toast.info("Creating video prompts...");
        await axios.post(`${API}/projects/${projectId}/video-prompts`);
        toast.success("Video prompts created!");
        
        toast.info("Generating video scenes...");
        await axios.post(`${API}/projects/${projectId}/generate-videos`);
        toast.success("Videos generated!");
        
        await fetchProject();
        setCurrentStep(3); // Audio step for V3
      } else if (videoType === "10_second_ad_v5") {
        // V5 pipeline: concepts → script → anchors → videos
        toast.info("Generating concept...");
        await axios.post(`${API}/projects/${projectId}/concepts-v5`);
        toast.success("Concept generated!");
        
        toast.info("Generating script...");
        await axios.post(`${API}/projects/${projectId}/auto-select-and-script-v5`);
        toast.success("Script generated!");
        
        toast.info("Creating anchor image prompts...");
        await axios.post(`${API}/projects/${projectId}/anchor-prompts`);
        toast.success("Anchor prompts created!");
        
        toast.info("Generating anchor images...");
        await axios.post(`${API}/projects/${projectId}/generate-anchor-images`, { approach: "gemini" });
        toast.success("Anchor images generated!");
        
        toast.info("Creating video prompts...");
        await axios.post(`${API}/projects/${projectId}/video-prompts`);
        toast.success("Video prompts created!");
        
        toast.info("Generating video scenes...");
        await axios.post(`${API}/projects/${projectId}/generate-videos`);
        toast.success("Videos generated!");
        
        await fetchProject();
        setCurrentStep(3); // Audio step for V5
      } else {
        // Basic flows (20s ad, 10s ad, 20s ad v2): generate concepts then advance
        toast.info("Generating concepts...");
        await axios.post(`${API}/projects/${projectId}/concepts`);
        toast.success("Concepts generated!");
        
        await fetchProject();
        setCurrentStep(3);
      }
    } catch (error) {
      console.error("Error saving analysis:", error);
      toast.error(error.response?.data?.detail || "Failed to continue");
    } finally {
      setSavingAnalysis(false);
      setProcessing(false);
    }
  };

  // Helper to update a field in the editable analysis
  const updateAnalysisField = (field, value) => {
    setEditableAnalysis(prev => ({ ...prev, [field]: value }));
    setAnalysisEdited(true);
  };

  // Helper to update array fields (newline separated text → array, preserves empty lines during editing)
  const updateAnalysisArrayField = (field, text) => {
    const items = text.split("\n");
    setEditableAnalysis(prev => ({ ...prev, [field]: items }));
    setAnalysisEdited(true);
  };

  // UGC Step 3: Handle Avatar Upload
  const handleAvatarUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!SUPPORTED_IMAGE_FORMATS.includes(ext)) {
      toast.error(`Unsupported format. Please use: ${SUPPORTED_IMAGE_FORMATS.join(', ')}`);
      return;
    }

    setUgcAvatarFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setUgcAvatarPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const uploadAvatarImage = async () => {
    // Handle existing avatar selection
    if (ugcAvatarMode === "existing") {
      if (!selectedDefaultAvatar) {
        toast.error("Please select an avatar");
        return;
      }
      // Studio avatars use `images: [url1, url2, url3]`; we use the first image (option 1a).
      const avatarUrl = selectedDefaultAvatar.images?.[0] || selectedDefaultAvatar.url;
      if (!avatarUrl) {
        toast.error("Selected avatar has no image");
        return;
      }
      
      setProcessing(true);
      try {
        await axios.post(`${API}/projects/${projectId}/ugc/set-avatar-url`, {
          avatar_url: avatarUrl
        }, { withCredentials: true });
        toast.success("Avatar selected!");
        await fetchProject();
        setCurrentStep(4); // Go to Product upload
      } catch (error) {
        console.error("Error setting avatar:", error);
        toast.error(error.response?.data?.detail || "Failed to set avatar");
      } finally {
        setProcessing(false);
      }
      return;
    }

    // Handle uploaded avatar
    if (!ugcAvatarFile) {
      toast.error("Please select an avatar image");
      return;
    }

    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('file', ugcAvatarFile);
      
      await axios.post(`${API}/projects/${projectId}/ugc/upload-avatar`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      toast.success("Avatar uploaded!");
      await fetchProject();
      setCurrentStep(4); // Go to Product upload
    } catch (error) {
      console.error("Error uploading avatar:", error);
      toast.error(error.response?.data?.detail || "Failed to upload avatar");
    } finally {
      setProcessing(false);
    }
  };

  // UGC Step 4: Handle Product Image Upload
  const handleProductUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!SUPPORTED_IMAGE_FORMATS.includes(ext)) {
      toast.error(`Unsupported format. Please use: ${SUPPORTED_IMAGE_FORMATS.join(', ')}`);
      return;
    }

    setUgcProductFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setUgcProductPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const uploadProductImage = async () => {
    if (!ugcProductFile) {
      toast.error("Please select a product image");
      return;
    }

    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('file', ugcProductFile);
      
      await axios.post(`${API}/projects/${projectId}/ugc/upload-product`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Product image uploaded!");
      await fetchProject();
      setCurrentStep(5); // Go to UGC Image Generation
    } catch (error) {
      console.error("Error uploading product image:", error);
      toast.error(error.response?.data?.detail || "Failed to upload product image");
    } finally {
      setProcessing(false);
    }
  };

  // UGC Step 5: Generate UGC Images
  const generateUGCImages = async () => {
    setProcessing(true);
    try {
      toast.info("Starting UGC image generation...");
      
      const response = await axios.post(`${API}/projects/${projectId}/ugc/generate-images`);
      
      if (!response.data.job_id) {
        throw new Error("Failed to start UGC image generation job");
      }
      
      toast.info("Generating UGC images... This may take a minute.");
      
      let attempts = 0;
      const maxAttempts = 60;
      setJobProgress({ progress: 5, message: "Starting UGC image generation..." });
      
      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 5000));
        
        try {
          const statusResponse = await axios.get(
            `${API}/projects/${projectId}/ugc-image-job-status?t=${Date.now()}`,
            { headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate', 'Pragma': 'no-cache' } }
          );
          const job = statusResponse.data;
          
          if (job.status === "completed") {
            setJobProgress({ progress: 100, message: "UGC images generated!" });
            const images = job.result?.images || [];
            setUgcGeneratedImages(images);
            toast.success("UGC images generated!");
            setJobProgress(null);
            return;
          } else if (job.status === "failed") {
            setJobProgress(null);
            toast.error(job.error || "UGC image generation failed");
            setProcessing(false);
            return;
          } else {
            setJobProgress({ progress: job.progress || 10, message: job.message || "Generating UGC images..." });
          }
        } catch (pollError) {
          if (pollError.message && !pollError.message.includes("Network Error")) {
            setJobProgress(null);
            throw pollError;
          }
          console.error("Error polling UGC image job status:", pollError);
        }
        
        attempts++;
      }
      
      setJobProgress(null);
      toast.error("UGC image generation timed out. Please refresh and try again.");
      await fetchProject();
      
    } catch (error) {
      console.error("Error generating UGC images:", error);
      toast.error(error.response?.data?.detail || error.message || "Failed to generate UGC images");
    } finally {
      setProcessing(false);
    }
  };

  const selectUGCImage = async (index) => {
    setUgcSelectedImageIndex(index);
    try {
      await axios.post(`${API}/projects/${projectId}/ugc/select-image`, {
        selected_image_index: index
      });
      await fetchProject();
    } catch (error) {
      console.error("Error selecting UGC image:", error);
    }
  };

  const continueAfterImageSelection = async () => {
    if (ugcSelectedImageIndex === null) {
      toast.error("Please select an image");
      return;
    }
    setCurrentStep(6); // Go to Script
  };

  // UGC Step 6: Generate Script
  const generateUGCScript = async () => {
    setProcessing(true);
    try {
      toast.info("Generating UGC script...");
      const response = await axios.post(`${API}/projects/${projectId}/ugc/generate-script`);
      setUgcScript(response.data.script);
      setEditableUgcScript(response.data.script?.voiceover_text || "");
      toast.success("Script generated!");
      await fetchProject();
    } catch (error) {
      console.error("Error generating UGC script:", error);
      toast.error("Failed to generate script");
    } finally {
      setProcessing(false);
    }
  };

  const continueToVideoGeneration = () => {
    if (!ugcScript) {
      toast.error("Please generate a script first");
      return;
    }
    setCurrentStep(7); // Go to Video Generation
  };

  // UGC Step 7: Generate Final UGC Video
  const generateUGCVideo = async () => {
    setProcessing(true);
    try {
      toast.info("Starting UGC video generation...");
      
      const response = await axios.post(`${API}/projects/${projectId}/ugc/generate-video`);
      
      if (!response.data.job_id) {
        throw new Error("Failed to start UGC video generation job");
      }
      
      toast.info("Generating UGC video... This may take a few minutes.");
      
      let attempts = 0;
      const maxAttempts = 120;
      setJobProgress({ progress: 5, message: "Starting UGC video generation..." });
      
      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 5000));
        
        try {
          const statusResponse = await axios.get(
            `${API}/projects/${projectId}/ugc-video-job-status?t=${Date.now()}`,
            { headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate', 'Pragma': 'no-cache' } }
          );
          const job = statusResponse.data;
          
          if (job.status === "completed") {
            setJobProgress({ progress: 100, message: "UGC video created!" });
            toast.success("UGC video created!");
            fireAdGenerated();
            setJobProgress(null);
            await fetchProject();
            setCurrentStep(8);
            return;
          } else if (job.status === "failed") {
            setJobProgress(null);
            toast.error(job.error || "UGC video generation failed");
            setProcessing(false);
            return;
          } else {
            setJobProgress({ progress: job.progress || 10, message: job.message || "Generating UGC video..." });
          }
        } catch (pollError) {
          if (pollError.message && !pollError.message.includes("Network Error")) {
            setJobProgress(null);
            throw pollError;
          }
          console.error("Error polling UGC video job status:", pollError);
        }
        
        attempts++;
      }
      
      setJobProgress(null);
      toast.error("UGC video generation timed out. Please refresh and check project status.");
      await fetchProject();
      
    } catch (error) {
      console.error("Error generating UGC video:", error);
      toast.error(error.response?.data?.detail || error.message || "Failed to generate UGC video");
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/projects')}
                className="rounded-full"
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
              </Button>
              <div>
                <h1 className="font-display text-xl font-semibold">{project?.name}</h1>
                {project?.product_analysis?.product_name && (
                  <p className="text-sm text-muted-foreground">{project.product_analysis.product_name}</p>
                )}
              </div>
            </div>
          </div>
          
          {/* Progress Steps - Centered */}
          <div className="flex justify-center">
            <div className="flex items-center gap-3 overflow-x-auto pb-2">
              {STEPS.map((step, index) => {
                const isCompleted = currentStep > step.id;
                const isActive = currentStep === step.id;
                const Icon = step.icon;
                const canNavigate = step.id <= highestStep && step.id !== currentStep;
                
                return (
                  <React.Fragment key={step.id}>
                    <div 
                      onClick={() => canNavigate && setCurrentStep(step.id)}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-full whitespace-nowrap transition-all duration-200 ${
                        isActive ? 'bg-primary text-white shadow-md shadow-primary/25' : 
                        canNavigate ? 'bg-primary/10 text-primary cursor-pointer hover:bg-primary/20' : 
                        'bg-secondary text-muted-foreground'
                      }`}
                      data-testid={`step-${step.id}`}
                    >
                      {isCompleted ? (
                        <Check className="w-4 h-4" strokeWidth={2} />
                      ) : (
                        <Icon className="w-4 h-4" strokeWidth={1.5} />
                      )}
                      <span className="text-sm font-medium">{step.label}</span>
                    </div>
                    {index < STEPS.length - 1 && (
                      <div className={`w-10 h-0.5 ${step.id < highestStep ? 'bg-primary' : 'bg-border'}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      
      {/* Background Job Progress Indicator */}
      <AnimatePresence>
        {jobProgress && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white border border-border rounded-xl shadow-2xl p-6 w-full max-w-sm space-y-4"
            >
              <div className="flex flex-col items-center gap-3 text-center">
                <Loader2 className="w-10 h-10 animate-spin text-primary" strokeWidth={1.5} />
                <p className="text-sm font-medium text-foreground" data-testid="job-progress-message">
                  {jobProgress.message}
                </p>
                <p className="text-xs text-muted-foreground">{jobProgress.progress}% complete</p>
              </div>
              <Progress value={jobProgress.progress} className="h-2" data-testid="job-progress-bar" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <AnimatePresence mode="wait">
          {/* Step 1: Video Type Selection */}
          {currentStep === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Film className="w-8 h-8 text-primary" strokeWidth={1.5} />
                </div>
                <h2 className="font-display text-2xl font-semibold mb-2">Choose Your Video Type</h2>
                <p className="text-muted-foreground">Select the format that best fits your campaign</p>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
                {/* UGC Multi-Scene */}
                <div
                  onClick={() => setVideoType("ugc_multi_video")}
                  className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all flex flex-col gap-2 ${
                    videoType === "ugc_multi_video" 
                      ? "border-primary bg-primary/5 shadow-md ring-2 ring-primary/30" 
                      : "border-primary/40 bg-primary/[0.02] hover:border-primary/60 ring-1 ring-primary/20"
                  }`}
                  data-testid="video-type-ugc-multi"
                >
                  <span className="absolute -top-2.5 left-3 bg-primary text-white text-[10px] px-2 py-0.5 rounded-full font-semibold tracking-wide">RECOMMENDED</span>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Film className="w-4 h-4 text-violet-500 shrink-0" />
                    <h3 className="font-semibold text-sm">UGC Multi-Scene</h3>
                  </div>
                  <p className="text-muted-foreground text-xs">4-scene UGC testimonial with consistent look</p>
                  <div className="grid grid-cols-2 gap-1 text-[11px] text-muted-foreground mt-auto">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />24s</span>
                    <span className="flex items-center gap-1"><Film className="w-3 h-3" />4 scenes</span>
                    <span className="flex items-center gap-1"><Sparkles className="w-3 h-3" />Premium</span>
                    <CreditBadge actionKey="assisted_ugc_multi" />
                  </div>
                </div>

                {/* UGC B-Roll */}
                <div
                  onClick={() => setVideoType("ugc_broll_video")}
                  className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all flex flex-col gap-2 ${
                    videoType === "ugc_broll_video" 
                      ? "border-primary bg-primary/5 shadow-md ring-2 ring-primary/30" 
                      : "border-emerald-400/40 bg-emerald-50/30 hover:border-emerald-400/60 ring-1 ring-emerald-400/20"
                  }`}
                  data-testid="video-type-ugc-broll"
                >
                  <span className="absolute -top-2.5 left-3 bg-emerald-500 text-white text-[10px] px-2 py-0.5 rounded-full font-semibold tracking-wide">POPULAR</span>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Clapperboard className="w-4 h-4 text-emerald-500 shrink-0" />
                    <h3 className="font-semibold text-sm">UGC B-Roll</h3>
                  </div>
                  <p className="text-muted-foreground text-xs">4-scene UGC with B-roll cutaways for visual variety</p>
                  <div className="grid grid-cols-2 gap-1 text-[11px] text-muted-foreground mt-auto">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />24s</span>
                    <span className="flex items-center gap-1"><Clapperboard className="w-3 h-3" />B-Roll</span>
                    <span className="flex items-center gap-1"><Sparkles className="w-3 h-3" />Premium</span>
                    <CreditBadge actionKey="assisted_ugc_broll" />
                  </div>
                </div>

                {/* 10 Second Ad */}
                <div
                  onClick={() => setVideoType("10_second_ad")}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all flex flex-col gap-2 ${
                    videoType === "10_second_ad" 
                      ? "border-primary bg-primary/5 shadow-md" 
                      : "border-border hover:border-primary/50"
                  }`}
                  data-testid="video-type-10s"
                >
                  <div className="flex items-center gap-2">
                    <Video className="w-4 h-4 text-orange-500 shrink-0" />
                    <h3 className="font-semibold text-sm">Quick Impact Ad</h3>
                    <span className="bg-orange-100 text-orange-700 text-[10px] px-1.5 py-0.5 rounded-full font-medium">NEW</span>
                  </div>
                  <p className="text-muted-foreground text-xs">Quick & punchy - ultra-short format</p>
                  <div className="grid grid-cols-2 gap-1 text-[11px] text-muted-foreground mt-auto">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />10s</span>
                    <span className="flex items-center gap-1"><Film className="w-3 h-3" />2 scenes</span>
                    <span className="flex items-center gap-1"><Smartphone className="w-3 h-3" />Standard</span>
                    <CreditBadge actionKey="assisted_10s_ad" />
                  </div>
                </div>

                {/* 20 Second Ad */}
                <div
                  onClick={() => setVideoType("20_second_ad")}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all flex flex-col gap-2 ${
                    videoType === "20_second_ad" 
                      ? "border-primary bg-primary/5 shadow-md" 
                      : "border-border hover:border-primary/50"
                  }`}
                  data-testid="video-type-20s"
                >
                  <div className="flex items-center gap-2">
                    <Video className="w-4 h-4 text-primary shrink-0" />
                    <h3 className="font-semibold text-sm">Performance Ad</h3>
                  </div>
                  <p className="text-muted-foreground text-xs">Perfect for Reels, Shorts & TikTok</p>
                  <div className="grid grid-cols-2 gap-1 text-[11px] text-muted-foreground mt-auto">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />20s</span>
                    <span className="flex items-center gap-1"><Film className="w-3 h-3" />4 scenes</span>
                    <span className="flex items-center gap-1"><Smartphone className="w-3 h-3" />Standard</span>
                    <CreditBadge actionKey="assisted_20s_ad" />
                  </div>
                </div>

                {/* 24s Premium Ad */}
                <div
                  onClick={() => setVideoType("24_second_ad_premium")}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all flex flex-col gap-2 ${
                    videoType === "24_second_ad_premium" 
                      ? "border-primary bg-primary/5 shadow-md" 
                      : "border-border hover:border-primary/50"
                  }`}
                  data-testid="video-type-24s-premium"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <Video className="w-4 h-4 text-primary shrink-0" />
                    <h3 className="font-semibold text-sm">Cinematic Ad Film</h3>
                    <span className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0.5 rounded-full font-medium">PREMIUM</span>
                  </div>
                  <p className="text-muted-foreground text-xs">Premium cinematic quality video ads</p>
                  <div className="grid grid-cols-2 gap-1 text-[11px] text-muted-foreground mt-auto">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />24s</span>
                    <span className="flex items-center gap-1"><Film className="w-3 h-3" />4 scenes</span>
                    <span className="flex items-center gap-1"><Sparkles className="w-3 h-3" />Premium</span>
                    <CreditBadge actionKey="assisted_24s_premium" />
                  </div>
                </div>

                {/* UGC Video */}
                <div
                  onClick={() => setVideoType("ugc_video")}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all flex flex-col gap-2 ${
                    videoType === "ugc_video" 
                      ? "border-primary bg-primary/5 shadow-md" 
                      : "border-border hover:border-primary/50"
                  }`}
                  data-testid="video-type-ugc"
                >
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-primary shrink-0" />
                    <h3 className="font-semibold text-sm">UGC Video</h3>
                    <span className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0.5 rounded-full font-medium">NEW</span>
                  </div>
                  <p className="text-muted-foreground text-xs">Authentic user-generated content style</p>
                  <div className="grid grid-cols-2 gap-1 text-[11px] text-muted-foreground mt-auto">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />6-7s</span>
                    <span className="flex items-center gap-1"><Film className="w-3 h-3" />1 scene</span>
                    <span className="flex items-center gap-1"><Sparkles className="w-3 h-3" />Premium</span>
                    <CreditBadge actionKey="assisted_ugc" />
                  </div>
                </div>

                {/* UGC Ad 20s Sora 2 */}
                <div
                  onClick={() => setVideoType("ugc_sora2_video")}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all flex flex-col gap-2 ${
                    videoType === "ugc_sora2_video" 
                      ? "border-primary bg-primary/5 shadow-md" 
                      : "border-border hover:border-primary/50"
                  }`}
                  data-testid="video-type-ugc-sora2"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <User className="w-4 h-4 text-primary shrink-0" />
                    <h3 className="font-semibold text-sm">Extended UGC Ad</h3>
                    <span className="bg-rose-100 text-rose-700 text-[10px] px-1.5 py-0.5 rounded-full font-medium">NEW</span>
                  </div>
                  <p className="text-muted-foreground text-xs">20-second AI-generated UGC testimonial</p>
                  <div className="grid grid-cols-2 gap-1 text-[11px] text-muted-foreground mt-auto">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />20s</span>
                    <span className="flex items-center gap-1"><Film className="w-3 h-3" />1 scene</span>
                    <span className="flex items-center gap-1"><Sparkles className="w-3 h-3" />Premium</span>
                    <CreditBadge actionKey="assisted_ugc_sora2" />
                  </div>
                </div>

                {/* Story UGC Ad — Sora 2 multi-scene */}
                <div
                  onClick={() => setVideoType("ugc_sora2_story")}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all flex flex-col gap-2 ${
                    videoType === "ugc_sora2_story"
                      ? "border-primary bg-primary/5 shadow-md"
                      : "border-border hover:border-primary/50"
                  }`}
                  data-testid="video-type-ugc-story"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <Film className="w-4 h-4 text-primary shrink-0" />
                    <h3 className="font-semibold text-sm">Story UGC Ad</h3>
                    <span className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0.5 rounded-full font-medium">NEW</span>
                  </div>
                  <p className="text-muted-foreground text-xs">20s narrative — Problem → Discovery → Result → CTA</p>
                  <div className="grid grid-cols-2 gap-1 text-[11px] text-muted-foreground mt-auto">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />20s</span>
                    <span className="flex items-center gap-1"><Film className="w-3 h-3" />4 scenes</span>
                    <span className="flex items-center gap-1"><Sparkles className="w-3 h-3" />Premium</span>
                    <CreditBadge actionKey="assisted_ugc_sora2_story" />
                  </div>
                </div>

                {/* 20 Seconds (Open Source) */}
                <div
                  onClick={() => setVideoType("20_second_ad_v2")}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all flex flex-col gap-2 ${
                    videoType === "20_second_ad_v2" 
                      ? "border-primary bg-primary/5 shadow-md" 
                      : "border-border hover:border-primary/50"
                  }`}
                  data-testid="video-type-20s-v2"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <Video className="w-4 h-4 text-primary shrink-0" />
                    <h3 className="font-semibold text-sm">Smart Budget Ad</h3>
                  </div>
                  <p className="text-muted-foreground text-xs">Enhanced video with open-source model</p>
                  <div className="grid grid-cols-2 gap-1 text-[11px] text-muted-foreground mt-auto">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />20s</span>
                    <span className="flex items-center gap-1"><Film className="w-3 h-3" />4 scenes</span>
                    <span className="flex items-center gap-1"><Smartphone className="w-3 h-3" />Cost-effective</span>
                    <CreditBadge actionKey="assisted_20s_opensource" />
                  </div>
                </div>
              </div>

                {/* 10 Second Ad Auto V5 Option - Hidden for launch
                <div
                  onClick={() => setVideoType("10_second_ad_v5")}
                  className={`p-6 rounded-xl border-2 cursor-pointer transition-all ${
                    videoType === "10_second_ad_v5" 
                      ? "border-primary bg-primary/5" 
                      : "border-border hover:border-primary/50"
                  }`}
                  data-testid="video-type-10s-v5"
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-1 ${videoType === "10_second_ad_v5" ? "border-primary bg-primary" : "border-muted-foreground"}`}>
                      {videoType === "10_second_ad_v5" && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Wand2 className="w-5 h-5 text-green-500" />
                        <h3 className="font-semibold text-lg">10 Sec Ad Auto V5</h3>
                        <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-medium">AUTO</span>
                        <span className="bg-orange-100 text-orange-700 text-xs px-2 py-0.5 rounded-full font-medium">NEW</span>
                      </div>
                      <p className="text-muted-foreground text-sm mb-3">Fully automated 10-second ad - URL to video in one click</p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Film className="w-3 h-3" />
                          2 × 5 seconds
                        </span>
                        <span className="flex items-center gap-1">
                          <Sparkles className="w-3 h-3" />
                          Auto Everything
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                */}

                {/* 20 Second Ad V3 Option - Hidden for launch
                <div
                  onClick={() => setVideoType("20_second_ad_v3")}
                  className={`p-6 rounded-xl border-2 cursor-pointer transition-all ${
                    videoType === "20_second_ad_v3" 
                      ? "border-primary bg-primary/5" 
                      : "border-border hover:border-primary/50"
                  }`}
                  data-testid="video-type-20s-v3"
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-1 ${videoType === "20_second_ad_v3" ? "border-primary bg-primary" : "border-muted-foreground"}`}>
                      {videoType === "20_second_ad_v3" && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Wand2 className="w-5 h-5 text-primary" />
                        <h3 className="font-semibold text-lg">20 Second Ad V3</h3>
                        <span className="bg-purple-100 text-purple-700 text-xs px-2 py-0.5 rounded-full font-medium">AUTO</span>
                      </div>
                      <p className="text-muted-foreground text-sm mb-3">Fully automated - scrapes images, single concept</p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Film className="w-3 h-3" />
                          4 × 5 seconds
                        </span>
                        <span className="flex items-center gap-1">
                          <Sparkles className="w-3 h-3" />
                          Auto Images
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                */}

                {/* Coming Soon */}
                <div className="p-4 rounded-xl border-2 border-dashed border-border bg-secondary/30 max-w-4xl mx-auto">
                  <h3 className="font-semibold text-sm text-muted-foreground mb-1">More formats coming soon...</h3>
                  <p className="text-xs text-muted-foreground/70">30 Second Ad, Product Demo, Story Sequence</p>
                </div>

                <Button
                  onClick={selectVideoType}
                  disabled={processing || !videoType}
                  className="w-full max-w-4xl mx-auto h-14 rounded-full bg-primary hover:opacity-90 text-lg font-medium gap-2"
                  data-testid="continue-video-type-btn"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      Continue
                      <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
                    </>
                  )}
                </Button>
            </motion.div>
          )}

          {/* Step 2: Product URL */}
          {currentStep === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              {!editableAnalysis && (
                <>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      {step2Mode === "url" ? (
                        <LinkIcon className="w-8 h-8 text-primary" strokeWidth={1.5} />
                      ) : (
                        <Pencil className="w-8 h-8 text-primary" strokeWidth={1.5} />
                      )}
                    </div>
                    <h2 className="font-display text-2xl font-semibold mb-2">
                      {step2Mode === "url" ? "Enter Product URL" : "Add Product Manually"}
                    </h2>
                    <p className="text-muted-foreground">
                      {step2Mode === "url" 
                        ? "Paste your e-commerce product link and we'll analyze it" 
                        : "Enter your product details and we'll generate the analysis"}
                    </p>
                  </div>

                  {/* Toggle: Paste URL / Add Manually */}
                  <div className="flex items-center justify-center gap-1 bg-secondary/70 rounded-full p-1 max-w-xs mx-auto" data-testid="step2-mode-toggle">
                    <button
                      onClick={() => setStep2Mode("url")}
                      className={`flex-1 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        step2Mode === "url" 
                          ? "bg-white text-foreground shadow-sm" 
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                      data-testid="step2-mode-url"
                    >
                      Paste URL
                    </button>
                    <button
                      onClick={() => setStep2Mode("manual")}
                      className={`flex-1 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        step2Mode === "manual" 
                          ? "bg-white text-foreground shadow-sm" 
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                      data-testid="step2-mode-manual"
                    >
                      Add Manually
                    </button>
                  </div>

                  {/* URL Mode */}
                  {step2Mode === "url" && (
                    <div className="max-w-xl mx-auto space-y-4">
                      <Input
                        placeholder="https://example.com/product"
                        value={productUrl}
                        onChange={(e) => setProductUrl(e.target.value)}
                        className="h-14 text-lg rounded-xl"
                        data-testid="product-url-input"
                      />
                      
                      {videoType === "20_second_ad_v3" && (
                        <p className="text-xs text-muted-foreground text-center">
                          Works best with Amazon, eBay, Walmart. Some sites (Flipkart, Shopsy) may require manual image upload via "20 Second Ad" flow.
                        </p>
                      )}
                      
                      {videoType === "10_second_ad_v5" && (
                        <p className="text-xs text-muted-foreground text-center">
                          Fully automated 10-second ad. Works best with Amazon, eBay, Walmart.
                        </p>
                      )}

                      <Button
                        onClick={isUgcType ? analyzeProductForUGC : 
                                 videoType === "20_second_ad_v3" ? analyzeProductV3 :
                                 videoType === "10_second_ad_v5" ? analyzeProductV5 : analyzeProduct}
                        disabled={processing || !productUrl.trim()}
                        className="w-full h-14 rounded-full bg-primary hover:opacity-90 text-lg font-medium gap-2"
                        data-testid="analyze-product-btn"
                      >
                        {processing ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Analyzing...
                          </>
                        ) : (
                          <>
                            Analyze Product
                            <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
                          </>
                        )}
                      </Button>
                    </div>
                  )}

                  {/* Manual Mode */}
                  {step2Mode === "manual" && (
                    <div className="max-w-xl mx-auto space-y-4">
                      <div>
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Product Name</label>
                        <Input
                          placeholder="e.g. Apple AirPods Pro 2"
                          value={manualProductName}
                          onChange={(e) => setManualProductName(e.target.value)}
                          className="mt-1 h-14 text-lg rounded-xl"
                          data-testid="manual-product-name"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Product Description</label>
                        <Textarea
                          placeholder="Describe the product — features, benefits, target audience, price, etc. The more detail you provide, the better the analysis."
                          value={manualProductDesc}
                          onChange={(e) => setManualProductDesc(e.target.value)}
                          rows={5}
                          className="mt-1 text-base rounded-xl"
                          data-testid="manual-product-desc"
                        />
                      </div>

                      <Button
                        onClick={analyzeProductManual}
                        disabled={processing || !manualProductName.trim() || !manualProductDesc.trim()}
                        className="w-full h-14 rounded-full bg-primary hover:opacity-90 text-lg font-medium gap-2"
                        data-testid="analyze-manual-btn"
                      >
                        {processing ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Analyzing...
                          </>
                        ) : (
                          <>
                            Analyze Product
                            <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </>
              )}

              {/* Product Analysis - Editable Form */}
              {editableAnalysis && (
                <div className="bg-white rounded-2xl border border-border shadow-sm max-w-xl mx-auto overflow-hidden" data-testid="editable-analysis-form">
                  {/* Header */}
                  <div className="px-7 pt-7 pb-5 border-b border-border/60 bg-gradient-to-r from-primary/[0.03] to-transparent">
                    <div className="flex items-center gap-3 mb-1">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Pencil className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="font-display text-lg font-semibold text-foreground">Product Analysis</h3>
                    </div>
                    <p className="text-sm text-muted-foreground ml-11">Review and edit before continuing</p>
                  </div>

                  <div className="px-7 py-6 space-y-7">
                    {/* Section 1: Product Details */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 mb-1">
                        <Package className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Product Details</span>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-foreground mb-1.5 block">Product Name</label>
                        <Input
                          value={editableAnalysis.product_name}
                          onChange={(e) => updateAnalysisField("product_name", e.target.value)}
                          className="h-11 bg-gray-50/80 border-gray-200 focus:bg-white transition-colors"
                          data-testid="analysis-product-name"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-foreground mb-1.5 block">Category</label>
                          <Input
                            value={editableAnalysis.category}
                            onChange={(e) => updateAnalysisField("category", e.target.value)}
                            className="h-11 bg-gray-50/80 border-gray-200 focus:bg-white transition-colors"
                            data-testid="analysis-category"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-foreground mb-1.5 block">Price Range</label>
                          <Input
                            value={editableAnalysis.price_range}
                            onChange={(e) => updateAnalysisField("price_range", e.target.value)}
                            className="h-11 bg-gray-50/80 border-gray-200 focus:bg-white transition-colors"
                            data-testid="analysis-price-range"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-foreground mb-1.5 block">Target Audience</label>
                        <Input
                          value={editableAnalysis.target_audience}
                          onChange={(e) => updateAnalysisField("target_audience", e.target.value)}
                          className="h-11 bg-gray-50/80 border-gray-200 focus:bg-white transition-colors"
                          data-testid="analysis-target-audience"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-foreground mb-1.5 block">Primary Use Case</label>
                        <Input
                          value={editableAnalysis.primary_use_case}
                          onChange={(e) => updateAnalysisField("primary_use_case", e.target.value)}
                          className="h-11 bg-gray-50/80 border-gray-200 focus:bg-white transition-colors"
                          data-testid="analysis-primary-use-case"
                        />
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-border/60" />

                    {/* Section 2: Marketing Insights */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 mb-1">
                        <Sparkles className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Marketing Insights</span>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-foreground mb-1.5 block">
                          Key Features <span className="text-muted-foreground font-normal">(one per line)</span>
                        </label>
                        <Textarea
                          value={editableAnalysis.key_features?.join("\n") || ""}
                          onChange={(e) => updateAnalysisArrayField("key_features", e.target.value)}
                          rows={3}
                          className="bg-gray-50/80 border-gray-200 focus:bg-white transition-colors resize-none"
                          placeholder="Feature 1&#10;Feature 2&#10;Feature 3"
                          data-testid="analysis-key-features"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-foreground mb-1.5 block">
                          Emotional Hooks <span className="text-muted-foreground font-normal">(one per line)</span>
                        </label>
                        <Textarea
                          value={editableAnalysis.emotional_hooks?.join("\n") || ""}
                          onChange={(e) => updateAnalysisArrayField("emotional_hooks", e.target.value)}
                          rows={3}
                          className="bg-gray-50/80 border-gray-200 focus:bg-white transition-colors resize-none"
                          placeholder="Hook 1&#10;Hook 2&#10;Hook 3"
                          data-testid="analysis-emotional-hooks"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-foreground mb-1.5 block">
                          Objections to Handle <span className="text-muted-foreground font-normal">(one per line)</span>
                        </label>
                        <Textarea
                          value={editableAnalysis.objections_to_handle?.join("\n") || ""}
                          onChange={(e) => updateAnalysisArrayField("objections_to_handle", e.target.value)}
                          rows={3}
                          className="bg-gray-50/80 border-gray-200 focus:bg-white transition-colors resize-none"
                          placeholder="Objection 1&#10;Objection 2"
                          data-testid="analysis-objections"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Footer CTA */}
                  <div className="px-7 pb-7 pt-1">
                    <Button
                      onClick={saveAnalysisAndContinue}
                      disabled={savingAnalysis || processing}
                      className="w-full h-14 rounded-full bg-primary hover:opacity-90 text-lg font-medium gap-2"
                      data-testid="save-analysis-continue-btn"
                    >
                      {savingAnalysis ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          {(videoType === "20_second_ad_v3" || videoType === "10_second_ad_v5") ? "Processing pipeline..." : "Saving & continuing..."}
                        </>
                      ) : (
                        <>
                          Save & Continue
                          <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Step 3: Concept Selection (20s Ad, 10s Ad) OR Avatar Upload (UGC) */}
          {currentStep === 3 && (videoType === "20_second_ad" || videoType === "20_second_ad_v2" || videoType === "10_second_ad" || videoType === "24_second_ad_premium") && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              {(() => {
                const conceptsLocked = !!(project?.script && project?.selected_concept_id);
                return (
                  <>
                    <div className="text-center">
                      <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                        <Sparkles className="w-8 h-8 text-primary" strokeWidth={1.5} />
                      </div>
                      <h2 className="font-display text-2xl font-semibold mb-2">
                        {conceptsLocked ? "Selected Concept" : "Choose Your Concept"}
                      </h2>
                      <p className="text-muted-foreground">
                        {conceptsLocked
                          ? "Concept is locked — script has already been generated"
                          : "Select the ad concept that best fits your brand"}
                      </p>
                    </div>

                    {/* Aspect Ratio Selector — only when unlocked */}
                    {!conceptsLocked && (
                      <div className="flex items-center justify-center gap-4">
                        <span className="text-sm text-muted-foreground">Aspect Ratio:</span>
                        <div className="flex gap-2">
                          <Button
                            variant={aspectRatio === "9:16" ? "default" : "outline"}
                            size="sm"
                            onClick={() => setAspectRatio("9:16")}
                            className="rounded-full gap-2"
                            data-testid="aspect-ratio-portrait-btn"
                          >
                            <Smartphone className="w-4 h-4" strokeWidth={1.5} />
                            9:16
                          </Button>
                          <Button
                            variant={aspectRatio === "16:9" ? "default" : "outline"}
                            size="sm"
                            onClick={() => setAspectRatio("16:9")}
                            className="rounded-full gap-2"
                            data-testid="aspect-ratio-landscape-btn"
                          >
                            <Monitor className="w-4 h-4" strokeWidth={1.5} />
                            16:9
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Concept Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {project?.concepts?.map((concept) => (
                        <div
                          key={concept.id}
                          onClick={() => !conceptsLocked && setSelectedConcept(concept.id)}
                          className={`concept-card bg-white rounded-xl border-2 p-6 transition-all ${
                            conceptsLocked
                              ? selectedConcept === concept.id
                                ? 'border-primary bg-primary/5'
                                : 'border-border opacity-50'
                              : `cursor-pointer ${selectedConcept === concept.id ? 'selected border-primary' : 'border-border hover:border-primary/50'}`
                          }`}
                          data-testid={`concept-card-${concept.id}`}
                        >
                          <div className="flex items-center justify-between mb-4">
                            <span className="font-mono text-xs text-muted-foreground">{concept.id}</span>
                            {selectedConcept === concept.id && (
                              <div className={`w-6 h-6 rounded-full flex items-center justify-center ${conceptsLocked ? 'bg-primary/80' : 'bg-primary'}`}>
                                {conceptsLocked ? <Lock className="w-3 h-3 text-white" /> : <Check className="w-4 h-4 text-white" strokeWidth={2} />}
                              </div>
                            )}
                          </div>
                          <h3 className="font-display text-lg font-semibold mb-2">{concept.title}</h3>
                          <p className="text-sm text-muted-foreground mb-3">{concept.hook}</p>
                          <div className="space-y-2 text-sm">
                            <p><span className="text-muted-foreground">Tone:</span> {concept.tone}</p>
                            <p className="text-xs text-muted-foreground">{concept.why_it_works}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Write Your Own Concept — only when unlocked */}
                    {!conceptsLocked && (
                      <div className="max-w-2xl mx-auto">
                        {!showCustomConcept ? (
                          <button
                            onClick={() => setShowCustomConcept(true)}
                            className="w-full border-2 border-dashed border-border rounded-xl p-5 flex items-center justify-center gap-2 text-muted-foreground hover:text-foreground hover:border-primary/50 transition-all"
                            data-testid="write-own-concept-btn"
                          >
                            <Plus className="w-5 h-5" />
                            Write Your Own Concept
                          </button>
                        ) : (
                          <div className="bg-white rounded-xl border-2 border-primary p-6 space-y-4" data-testid="custom-concept-form">
                            <div className="flex items-center justify-between">
                              <h3 className="font-display font-semibold">Your Concept</h3>
                              <button onClick={() => setShowCustomConcept(false)} className="text-muted-foreground hover:text-foreground">
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                            <div>
                              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Title</label>
                              <Input
                                placeholder="e.g. Bold First Impression"
                                value={customConcept.title}
                                onChange={(e) => setCustomConcept(prev => ({ ...prev, title: e.target.value }))}
                                className="mt-1"
                                data-testid="custom-concept-title"
                              />
                            </div>
                            <div>
                              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Idea</label>
                              <Input
                                placeholder="e.g. What if your morning routine changed everything?"
                                value={customConcept.hook}
                                onChange={(e) => setCustomConcept(prev => ({ ...prev, hook: e.target.value }))}
                                className="mt-1"
                                data-testid="custom-concept-hook"
                              />
                            </div>
                            <div>
                              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Tone</label>
                              <Input
                                placeholder="e.g. Aspirational, Warm, Urgent"
                                value={customConcept.tone}
                                onChange={(e) => setCustomConcept(prev => ({ ...prev, tone: e.target.value }))}
                                className="mt-1"
                                data-testid="custom-concept-tone"
                              />
                            </div>
                            <Button
                              onClick={saveCustomConcept}
                              disabled={savingCustomConcept || !customConcept.title.trim() || !customConcept.hook.trim() || !customConcept.tone.trim()}
                              className="w-full rounded-full bg-primary hover:opacity-90 gap-2"
                              data-testid="save-custom-concept-btn"
                            >
                              {savingCustomConcept ? (
                                <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</>
                              ) : (
                                "Save & Select This Concept"
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Action buttons — only when unlocked */}
                    {!conceptsLocked && (
                      <div className="flex flex-col items-center gap-3">
                        <Button
                          onClick={selectConcept}
                          disabled={processing || !selectedConcept}
                          className="px-12 h-14 rounded-full bg-primary hover:opacity-90 text-lg font-medium gap-2"
                          data-testid="select-concept-btn"
                        >
                          {processing ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin" />
                              Processing...
                            </>
                          ) : (
                            <>
                              Continue
                              <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
                            </>
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={regenerateConcepts}
                          disabled={regeneratingConcepts || processing}
                          className="text-muted-foreground hover:text-foreground gap-2"
                          data-testid="regenerate-concepts-btn"
                        >
                          {regeneratingConcepts ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="w-4 h-4" />
                              Generate New Concepts
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </>
                );
              })()}
            </motion.div>
          )}

          {/* UGC Step 3: Avatar Selection */}
          {currentStep === 3 && (isUgcType) && (
            <motion.div
              key="ugc-step3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <User className="w-8 h-8 text-primary" strokeWidth={1.5} />
                </div>
                <h2 className="font-display text-2xl font-semibold mb-2">Choose Your Avatar</h2>
                <p className="text-muted-foreground">Select from our library or upload your own avatar</p>
              </div>

              {/* Avatar Mode Toggle */}
              <div className="flex justify-center gap-4 max-w-md mx-auto">
                <button
                  onClick={() => {
                    setUgcAvatarMode("existing");
                    setUgcAvatarFile(null);
                    setUgcAvatarPreview(null);
                  }}
                  className={`flex-1 py-3 px-4 rounded-xl border-2 transition-all ${
                    ugcAvatarMode === "existing"
                      ? "border-primary bg-primary/5 text-primary"
                      : "border-border hover:border-primary/50"
                  }`}
                  data-testid="avatar-mode-existing"
                >
                  <div className="flex items-center justify-center gap-2">
                    <User className="w-5 h-5" />
                    <span className="font-medium">Choose Existing</span>
                  </div>
                </button>
                <button
                  onClick={() => {
                    setUgcAvatarMode("upload");
                    setSelectedDefaultAvatar(null);
                  }}
                  className={`flex-1 py-3 px-4 rounded-xl border-2 transition-all ${
                    ugcAvatarMode === "upload"
                      ? "border-primary bg-primary/5 text-primary"
                      : "border-border hover:border-primary/50"
                  }`}
                  data-testid="avatar-mode-upload"
                >
                  <div className="flex items-center justify-center gap-2">
                    <Upload className="w-5 h-5" />
                    <span className="font-medium">Upload New</span>
                  </div>
                </button>
              </div>

              <div className="max-w-2xl mx-auto">
                {/* Existing Avatars Grid — pulled from Avatar Studio library */}
                {ugcAvatarMode === "existing" && (
                  <div className="space-y-6">
                    {studioAvatarsLoading && (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-6 h-6 animate-spin text-primary" />
                      </div>
                    )}

                    {!studioAvatarsLoading && studioAvatars.length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        No avatars found in your library yet.
                      </p>
                    )}

                    {!studioAvatarsLoading && studioAvatars.length > 0 && (() => {
                      const customAvatars = studioAvatars.filter(a => !a.is_preset);
                      const presetAvatars = studioAvatars.filter(a => a.is_preset);
                      const filteredPresets = avatarRegionFilter === "all"
                        ? presetAvatars
                        : presetAvatars.filter(a => (a.region || "").toLowerCase() === avatarRegionFilter);

                      const renderAvatarCard = (avatar) => {
                        const cover = avatar.images?.[0] || avatar.url;
                        const isSelected = selectedDefaultAvatar?.id === avatar.id;
                        return (
                          <div
                            key={avatar.id}
                            onClick={() => setSelectedDefaultAvatar(avatar)}
                            className={`relative cursor-pointer rounded-xl overflow-hidden border-2 transition-all ${
                              isSelected
                                ? "border-primary ring-2 ring-primary ring-offset-2"
                                : "border-transparent hover:border-primary/50"
                            }`}
                            data-testid={`default-avatar-${avatar.id}`}
                          >
                            <div className="aspect-square bg-muted">
                              {cover && (
                                <img
                                  src={cover}
                                  alt={avatar.name}
                                  className="w-full h-full object-cover"
                                />
                              )}
                            </div>
                            {isSelected && (
                              <div className="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                                <Check className="w-4 h-4 text-white" />
                              </div>
                            )}
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                              <p className="text-white text-xs font-medium line-clamp-1">{avatar.name}</p>
                            </div>
                          </div>
                        );
                      };

                      return (
                        <>
                          {/* My Avatars — user-created custom avatars */}
                          {customAvatars.length > 0 && (
                            <div className="space-y-3">
                              <div className="flex items-center gap-2">
                                <h3 className="text-sm font-semibold text-foreground">My Avatars</h3>
                                <span className="text-xs text-muted-foreground">({customAvatars.length})</span>
                              </div>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                {customAvatars.map(renderAvatarCard)}
                              </div>
                            </div>
                          )}

                          {/* Presets */}
                          {presetAvatars.length > 0 && (
                            <div className="space-y-3">
                              <div className="flex items-center justify-between flex-wrap gap-2">
                                <h3 className="text-sm font-semibold text-foreground">Presets</h3>
                                <div className="flex items-center gap-2" data-testid="avatar-region-filter">
                                  {["all", "american", "indian"].map(r => (
                                    <button
                                      key={r}
                                      onClick={() => setAvatarRegionFilter(r)}
                                      className={`px-3 py-1 rounded-full text-xs font-medium capitalize transition-all ${
                                        avatarRegionFilter === r
                                          ? "bg-primary text-primary-foreground"
                                          : "bg-muted text-muted-foreground hover:bg-muted/80"
                                      }`}
                                      data-testid={`avatar-region-${r}`}
                                    >
                                      {r}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              {filteredPresets.length === 0 ? (
                                <p className="text-xs text-muted-foreground text-center py-6">No {avatarRegionFilter} presets available.</p>
                              ) : (
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                  {filteredPresets.map(renderAvatarCard)}
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Upload Avatar */}
                {ugcAvatarMode === "upload" && (
                  <label className="upload-zone flex flex-col items-center justify-center p-12 rounded-xl cursor-pointer hover:border-primary border-2 border-dashed border-border">
                    {ugcAvatarPreview ? (
                      <div className="w-48 h-48 rounded-xl overflow-hidden mb-4">
                        <img src={ugcAvatarPreview} alt="Avatar preview" className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <User className="w-16 h-16 text-muted-foreground mb-4" strokeWidth={1} />
                    )}
                    <p className="text-lg font-medium mb-1">{ugcAvatarPreview ? "Click to change" : "Upload avatar image"}</p>
                    <p className="text-sm text-muted-foreground">Clear face photo for best results</p>
                    <input
                      type="file"
                      accept=".png,.jpg,.jpeg,.webp,.gif"
                      onChange={handleAvatarUpload}
                      className="hidden"
                      data-testid="ugc-avatar-input"
                    />
                  </label>
                )}

                <Button
                  onClick={uploadAvatarImage}
                  disabled={processing || (ugcAvatarMode === "existing" ? !selectedDefaultAvatar : !ugcAvatarFile)}
                  className="w-full h-14 rounded-full bg-primary hover:opacity-90 text-lg font-medium gap-2 mt-6"
                  data-testid="upload-avatar-btn"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {ugcAvatarMode === "existing" ? "Selecting..." : "Uploading..."}
                    </>
                  ) : (
                    <>
                      Continue
                      <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          )}

          {/* UGC Step 4: Product Image Upload */}
          {currentStep === 4 && (isUgcType) && (
            <motion.div
              key="ugc-step4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Package className="w-8 h-8 text-primary" strokeWidth={1.5} />
                </div>
                <h2 className="font-display text-2xl font-semibold mb-2">Upload Product Image</h2>
                <p className="text-muted-foreground">Upload a clear image of the product</p>
              </div>

              <div className="max-w-xl mx-auto">
                <label className="upload-zone flex flex-col items-center justify-center p-12 rounded-xl cursor-pointer hover:border-primary border-2 border-dashed border-border">
                  {ugcProductPreview ? (
                    <div className="w-48 h-48 rounded-xl overflow-hidden mb-4">
                      <img src={ugcProductPreview} alt="Product preview" className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <Package className="w-16 h-16 text-muted-foreground mb-4" strokeWidth={1} />
                  )}
                  <p className="text-lg font-medium mb-1">{ugcProductPreview ? "Click to change" : "Upload product image"}</p>
                  <p className="text-sm text-muted-foreground">Clear product shot on white background works best</p>
                  <input
                    type="file"
                    accept=".png,.jpg,.jpeg,.webp,.gif"
                    onChange={handleProductUpload}
                    className="hidden"
                    data-testid="ugc-product-input"
                  />
                </label>

                <Button
                  onClick={uploadProductImage}
                  disabled={processing || !ugcProductFile}
                  className="w-full h-14 rounded-full bg-primary hover:opacity-90 text-lg font-medium gap-2 mt-6"
                  data-testid="upload-product-btn"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      Continue
                      <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          )}

          {/* UGC Step 5: Generate & Select UGC Images */}
          {currentStep === 5 && (videoType === "ugc_video" || videoType === "ugc_sora2_video") && (
            <motion.div
              key="ugc-step5"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <ImageIcon className="w-8 h-8 text-primary" strokeWidth={1.5} />
                </div>
                <h2 className="font-display text-2xl font-semibold mb-2">UGC Image Options</h2>
                <p className="text-muted-foreground">Generate and select the best UGC-style image</p>
              </div>

              <div className="max-w-3xl mx-auto">
                {ugcGeneratedImages.length === 0 ? (
                  <div className="text-center py-12">
                    <Wand2 className="w-16 h-16 text-muted-foreground mx-auto mb-4" strokeWidth={1} />
                    <p className="text-muted-foreground mb-6">Generate 4 UGC-style images of your avatar with the product</p>
                    <Button
                      onClick={generateUGCImages}
                      disabled={processing}
                      className="px-12 h-14 rounded-full bg-primary hover:opacity-90 text-lg font-medium gap-2"
                      data-testid="generate-ugc-images-btn"
                    >
                      {processing ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Generating Images...
                        </>
                      ) : (
                        <>
                          <Wand2 className="w-5 h-5" strokeWidth={1.5} />
                          Generate UGC Images
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {ugcGeneratedImages.map((img, i) => (
                        <div
                          key={i}
                          onClick={() => selectUGCImage(i)}
                          className={`aspect-[9/16] rounded-xl overflow-hidden cursor-pointer border-4 transition-all ${
                            ugcSelectedImageIndex === i ? 'border-primary shadow-lg' : 'border-transparent hover:border-primary/50'
                          }`}
                          data-testid={`ugc-image-${i}`}
                        >
                          {img.image_path ? (
                            <img 
                              src={`${API}/files/${img.image_path.split('/').pop()}`}
                              alt={`UGC Option ${i + 1}`}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-secondary flex items-center justify-center">
                              <p className="text-muted-foreground text-sm">Failed</p>
                            </div>
                          )}
                          {ugcSelectedImageIndex === i && (
                            <div className="absolute top-2 right-2 w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                              <Check className="w-5 h-5 text-white" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8">
                      <Button
                        onClick={generateUGCImages}
                        disabled={processing}
                        variant="outline"
                        className="px-8 h-12 rounded-full font-medium gap-2"
                        data-testid="regenerate-ugc-images-btn"
                      >
                        {processing ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Regenerating...
                          </>
                        ) : (
                          <>
                            <Wand2 className="w-4 h-4" />
                            Regenerate
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={continueAfterImageSelection}
                        disabled={ugcSelectedImageIndex === null}
                        className="px-12 h-12 rounded-full bg-primary hover:opacity-90 font-medium gap-2"
                        data-testid="continue-after-image-btn"
                      >
                        Continue
                        <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          )}

          {/* UGC Step 6: Script Generation */}
          {currentStep === 6 && (videoType === "ugc_video" || videoType === "ugc_sora2_video") && (
            <motion.div
              key="ugc-step6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <FileText className="w-8 h-8 text-primary" strokeWidth={1.5} />
                </div>
                <h2 className="font-display text-2xl font-semibold mb-2">UGC Script</h2>
                <p className="text-muted-foreground">
                  {videoType === "ugc_sora2_video" 
                    ? "Generate and edit a 19-second script for your UGC ad" 
                    : "Generate a short, authentic script for your UGC video"}
                </p>
              </div>

              <div className="max-w-xl mx-auto">
                {!ugcScript ? (
                  <div className="text-center py-12">
                    <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" strokeWidth={1} />
                    <p className="text-muted-foreground mb-6">
                      {videoType === "ugc_sora2_video" ? "Generate a 19-second UGC-style script" : "Generate a 6-7 second UGC-style script"}
                    </p>
                    <Button
                      onClick={generateUGCScript}
                      disabled={processing}
                      className="px-12 h-14 rounded-full bg-primary hover:opacity-90 text-lg font-medium gap-2"
                      data-testid="generate-ugc-script-btn"
                    >
                      {processing ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Generating Script...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-5 h-5" strokeWidth={1.5} />
                          Generate Script
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="bg-white rounded-xl border border-border p-6 space-y-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="bg-primary/10 text-primary text-xs px-3 py-1 rounded-full font-medium">
                          {ugcScript.duration}
                        </span>
                        <span className="bg-secondary text-muted-foreground text-xs px-3 py-1 rounded-full">
                          Tone: {ugcScript.tone}
                        </span>
                        {videoType === "ugc_sora2_video" && (
                          <span className="bg-rose-100 text-rose-700 text-xs px-3 py-1 rounded-full font-medium">
                            Editable
                          </span>
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Script</p>
                        {videoType === "ugc_sora2_video" ? (
                          <div className="space-y-2">
                            <textarea
                              value={editableUgcScript}
                              onChange={(e) => setEditableUgcScript(e.target.value)}
                              className="w-full min-h-[120px] p-3 border border-border rounded-lg text-base font-medium resize-y focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                              placeholder="Edit your script here..."
                              data-testid="ugc-sora2-script-textarea"
                            />
                            <div className="flex items-center justify-between text-xs text-muted-foreground">
                              <span>{editableUgcScript.trim().split(/\s+/).filter(Boolean).length} words</span>
                              <span className="text-muted-foreground/70">Target: ~55-60 words for 19 seconds</span>
                            </div>
                            {editableUgcScript !== ugcScript.voiceover_text && (
                              <Button
                                onClick={async () => {
                                  try {
                                    await axios.put(`${API}/projects/${projectId}/ugc-script`, {
                                      voiceover_text: editableUgcScript
                                    });
                                    setUgcScript({ ...ugcScript, voiceover_text: editableUgcScript });
                                    toast.success("Script saved!");
                                  } catch (err) {
                                    toast.error("Failed to save script");
                                  }
                                }}
                                size="sm"
                                className="rounded-full px-6 gap-1"
                                data-testid="save-ugc-script-btn"
                              >
                                <Check className="w-3 h-3" />
                                Save Changes
                              </Button>
                            )}
                          </div>
                        ) : (
                          <p className="text-lg font-medium">"{ugcScript.voiceover_text}"</p>
                        )}
                      </div>
                      {ugcScript.delivery_notes && (
                        <div>
                          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Delivery Notes</p>
                          <p className="text-sm text-muted-foreground">{ugcScript.delivery_notes}</p>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col sm:flex-row justify-center gap-4 mt-6">
                      <Button
                        onClick={generateUGCScript}
                        disabled={processing}
                        variant="outline"
                        className="px-8 h-12 rounded-full font-medium gap-2"
                        data-testid="regenerate-ugc-script-btn"
                      >
                        {processing ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Regenerating...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4" />
                            Regenerate
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={continueToVideoGeneration}
                        className="px-12 h-12 rounded-full bg-primary hover:opacity-90 font-medium gap-2"
                        data-testid="continue-to-video-btn"
                      >
                        Continue
                        <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          )}

          {/* UGC Step 7: Video Generation */}
          {currentStep === 7 && (videoType === "ugc_video" || videoType === "ugc_sora2_video") && (
            <motion.div
              key="ugc-step7"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Wand2 className="w-8 h-8 text-primary" strokeWidth={1.5} />
                </div>
                <h2 className="font-display text-2xl font-semibold mb-2">Generate UGC Video</h2>
                <p className="text-muted-foreground">Create your final UGC-style video</p>
              </div>

              <div className="max-w-xl mx-auto text-center">
                <div className="bg-secondary/50 rounded-xl p-6 mb-6">
                  <h3 className="font-medium mb-4">Ready to generate your video</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-muted-foreground text-xs mb-1">Selected Image</p>
                      {project?.ugc_selected_image && (
                        <img 
                          src={`${API}/files/${project.ugc_selected_image.split('/').pop()}`}
                          alt="Selected UGC"
                          className="w-full aspect-[9/16] object-cover rounded-lg"
                        />
                      )}
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-muted-foreground text-xs mb-1">Script</p>
                      <p className="text-sm font-medium">"{ugcScript?.voiceover_text || project?.ugc_script?.voiceover_text}"</p>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={generateUGCVideo}
                  disabled={processing}
                  className="px-12 h-14 rounded-full bg-primary hover:opacity-90 text-lg font-medium gap-2"
                  data-testid="generate-ugc-video-btn"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Generating Video... (This may take a few minutes)
                    </>
                  ) : (
                    <>
                      <Video className="w-5 h-5" strokeWidth={1.5} />
                      Generate UGC Video
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          )}

          {/* UGC Step 8: Final Video */}
          {currentStep === 8 && (videoType === "ugc_video" || videoType === "ugc_sora2_video") && (
            <motion.div
              key="ugc-step8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Check className="w-8 h-8 text-green-600" strokeWidth={2} />
                </div>
                <h2 className="font-display text-2xl font-semibold mb-2">Your UGC Video is Ready!</h2>
                <p className="text-muted-foreground">6-7 second UGC-style video for {project?.product_analysis?.product_name}</p>
              </div>

              <div className="max-w-md mx-auto">
                <div className="aspect-[9/16] rounded-2xl overflow-hidden bg-black shadow-float">
                  {project?.ugc_final_video ? (
                    <video 
                      src={`${API}/files/${project.ugc_final_video.split('/').pop()}`}
                      className="w-full h-full"
                      controls
                      autoPlay
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center min-h-[400px]">
                      <p className="text-white/50">Video not available</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                {project?.ugc_final_video && (
                  <Button
                    className="px-8 h-12 rounded-full bg-primary hover:opacity-90 font-medium gap-2"
                    data-testid="download-ugc-video-btn"
                    onClick={() => {
                      const a = document.createElement("a");
                      a.href = `${API}/download-video?url=${encodeURIComponent(project.ugc_final_video)}`;
                      a.download = project.ugc_final_video.split("/").pop()?.split("?")[0] || "video.mp4";
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                    }}
                  >
                    <Download className="w-4 h-4" strokeWidth={1.5} />
                    Download Video
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => navigate('/projects')}
                  className="px-8 h-12 rounded-full font-medium"
                  data-testid="back-to-projects-ugc-btn"
                >
                  Back to Projects
                </Button>
              </div>

              {/* Subtitled video display */}
              {project?.final_video_subtitled_scribe_url && (
                <div className="max-w-md mx-auto space-y-3">
                  <h3 className="text-sm font-semibold text-center">With Subtitles</h3>
                  <div className="aspect-[9/16] rounded-2xl overflow-hidden bg-black shadow-float">
                    <video src={`${project.final_video_subtitled_scribe_url.startsWith('/') ? API + '/files/' + project.final_video_subtitled_scribe_url.split('/').pop() : project.final_video_subtitled_scribe_url}?t=${Date.now()}`} className="w-full h-full" controls data-testid="ugc-subtitled-video" />
                  </div>
                  <div className="flex justify-center">
                    <Button size="sm" className="rounded-full bg-primary hover:opacity-90 font-medium gap-2" data-testid="download-ugc-subtitled-btn" onClick={() => {
                      const url = project.final_video_subtitled_scribe_url;
                      const a = document.createElement("a");
                      a.href = `${API}/download-video?url=${encodeURIComponent(url)}`;
                      a.download = "ugc-subtitled.mp4";
                      document.body.appendChild(a); a.click(); document.body.removeChild(a);
                    }}>
                      <Download className="w-4 h-4" />Download Subtitled
                    </Button>
                  </div>
                </div>
              )}

              {/* Add Subtitles section */}
              {project?.ugc_final_video && (
                <div className="max-w-2xl mx-auto border-t border-border pt-6">
                  <h3 className="text-sm font-semibold text-center mb-4">Add Subtitles</h3>

                  <div className="grid grid-cols-2 gap-3 max-w-md mx-auto mb-5">
                    <button onClick={() => setSubtitleStyle("bold_pop")} className={`relative p-4 rounded-xl border-2 transition-all text-center ${subtitleStyle === "bold_pop" ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`} data-testid="ugc-style-bold-pop">
                      {subtitleStyle === "bold_pop" && <div className="absolute top-2 right-2"><Check className="w-4 h-4 text-primary" /></div>}
                      <div className="bg-black rounded-lg p-3 mb-2 aspect-video flex items-center justify-center">
                        <span className="text-white font-black text-lg tracking-wide">CHANGED</span>
                      </div>
                      <p className="text-xs font-semibold">Bold Pop</p>
                      <p className="text-[10px] text-muted-foreground">Large, 1-2 words at a time</p>
                    </button>
                    <button onClick={() => setSubtitleStyle("karaoke")} className={`relative p-4 rounded-xl border-2 transition-all text-center ${subtitleStyle === "karaoke" ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`} data-testid="ugc-style-karaoke">
                      {subtitleStyle === "karaoke" && <div className="absolute top-2 right-2"><Check className="w-4 h-4 text-primary" /></div>}
                      <div className="bg-black rounded-lg p-3 mb-2 aspect-video flex items-center justify-center">
                        <span className="text-white/60 text-sm font-bold">This </span>
                        <span className="text-yellow-400 text-sm font-bold">ONE </span>
                        <span className="text-white/60 text-sm font-bold">changed</span>
                      </div>
                      <p className="text-xs font-semibold">Karaoke</p>
                      <p className="text-[10px] text-muted-foreground">Word-by-word highlight</p>
                    </button>
                  </div>

                  {jobProgress && (
                    <div className="max-w-md mx-auto space-y-3 mb-4">
                      <div className="h-3 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${jobProgress.progress}%` }} />
                      </div>
                      <p className="text-sm text-center text-muted-foreground">{jobProgress.message}</p>
                    </div>
                  )}

                  <div className="flex justify-center">
                    <Button variant="outline" className="rounded-full gap-2" data-testid="ugc-subtitle-btn" disabled={processing || !!jobProgress}
                      onClick={async () => {
                        try {
                          setProcessing(true);
                          toast.info("Generating subtitles...");
                          const res = await axios.post(`${API}/projects/${projectId}/add-subtitles`, { method: "scribe", style: subtitleStyle }, { withCredentials: true });
                          if (!res.data.job_id) throw new Error("Failed to start subtitle generation");
                          let attempts = 0;
                          setJobProgress({ progress: 5, message: "Transcribing audio with AI..." });
                          while (attempts < 60) {
                            await new Promise(resolve => setTimeout(resolve, 5000));
                            try {
                              const statusRes = await axios.get(`${API}/projects/${projectId}/subtitle-job-status?t=${Date.now()}`, { withCredentials: true });
                              const job = statusRes.data;
                              if (job.status === "completed") { toast.success("Subtitles added!"); setJobProgress(null); await fetchProject(); return; }
                              else if (job.status === "failed") { setJobProgress(null); toast.error(job.error || "Failed"); return; }
                              else { setJobProgress({ progress: job.progress || 10, message: job.message || "Processing..." }); }
                            } catch (pollErr) { if (!pollErr.message?.includes("Network Error")) { setJobProgress(null); throw pollErr; } }
                            attempts++;
                          }
                          setJobProgress(null); toast.error("Timed out.");
                        } catch (e) { toast.error(e.response?.data?.detail || e.message || "Failed"); } finally { setProcessing(false); setJobProgress(null); }
                      }}
                    >
                      <Sparkles className="w-4 h-4" />Add Subtitles
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* ============ UGC MULTI-SCENE STEPS ============ */}

          {/* Step 5: Generate 8 Images */}
          {currentStep === 5 && (videoType === "ugc_multi_video" || videoType === "ugc_broll_video" || videoType === "ugc_sora2_story") && (
            <motion.div key="ugc-multi-step5" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <ImageIcon className="w-8 h-8 text-primary" strokeWidth={1.5} />
                </div>
                <h2 className="font-display text-2xl font-semibold mb-2">Generate UGC Images</h2>
                <p className="text-muted-foreground text-sm">{isUgcStoryType ? "Generate 4 candidate UGC images of your avatar with the product" : "Generate 8 images of your avatar with the product in different camera angles"}</p>
              </div>

              {/* Job progress bar */}
              {jobProgress && (
                <div className="max-w-md mx-auto space-y-3">
                  <div className="h-3 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${jobProgress.progress}%` }} />
                  </div>
                  <p className="text-sm text-center text-muted-foreground">{jobProgress.message}</p>
                </div>
              )}

              {ugcMultiImages.length === 0 ? (
                <div className="text-center py-8">
                  <Button
                    onClick={async () => {
                      try {
                        setProcessing(true);
                        const epPrefix = isUgcStoryType ? "ugc-story" : "ugc-multi";
                        const imgCount = isUgcStoryType ? 4 : 8;
                        toast.info("Starting UGC image generation...");
                        const res = await axios.post(`${API}/projects/${projectId}/${epPrefix}/generate-images`, {}, { withCredentials: true });
                        if (!res.data.job_id) throw new Error("Failed to start image generation");
                        toast.info(`Generating ${imgCount} UGC images... This may take a minute.`);

                        let attempts = 0;
                        const maxAttempts = 60;
                        setJobProgress({ progress: 5, message: "Starting UGC image generation..." });

                        while (attempts < maxAttempts) {
                          await new Promise(resolve => setTimeout(resolve, 5000));
                          try {
                            const statusRes = await axios.get(
                              `${API}/projects/${projectId}/${epPrefix}-image-job-status?t=${Date.now()}`,
                              { headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate', 'Pragma': 'no-cache' }, withCredentials: true }
                            );
                            const job = statusRes.data;
                            if (job.status === "completed") {
                              setJobProgress({ progress: 100, message: "Images generated!" });
                              const images = job.result?.images || [];
                              setUgcMultiImages(images);
                              toast.success(`${imgCount} images generated!`);
                              setJobProgress(null);
                              return;
                            } else if (job.status === "failed") {
                              setJobProgress(null);
                              toast.error(job.error || "Image generation failed");
                              return;
                            } else {
                              setJobProgress({ progress: job.progress || 10, message: job.message || "Generating images..." });
                            }
                          } catch (pollError) {
                            if (pollError.message && !pollError.message.includes("Network Error")) {
                              setJobProgress(null);
                              throw pollError;
                            }
                          }
                          attempts++;
                        }
                        setJobProgress(null);
                        toast.error("Image generation timed out. Please refresh and try again.");
                        await fetchProject();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || e.message || "Failed to generate images");
                      } finally {
                        setProcessing(false);
                        setJobProgress(null);
                      }
                    }}
                    disabled={processing || !!jobProgress}
                    className="px-8 h-12 rounded-full bg-primary hover:opacity-90 font-medium gap-2"
                    data-testid="generate-ugc-multi-images-btn"
                  >
                    {processing ? <><Loader2 className="w-5 h-5 animate-spin" />Generating 8 images...</> : <><Wand2 className="w-5 h-5" />Generate 8 UGC Images</>}
                  </Button>
                </div>
              ) : (
                <div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {ugcMultiImages.map((img, i) => (
                      <div key={i} className="aspect-[9/16] rounded-xl overflow-hidden border border-border" data-testid={`ugc-multi-img-${i}`}>
                        {img.image_path ? (
                          <img src={`${img.image_path}?t=${Date.now()}`} alt={`Angle ${i+1}`} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-muted"><p className="text-xs text-muted-foreground">Failed</p></div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-center gap-3 mt-6">
                    <Button variant="outline" onClick={async () => {
                      try {
                        setProcessing(true);
                        toast.info("Regenerating 8 images...");
                        const res = await axios.post(`${API}/projects/${projectId}/ugc-multi/generate-images`, {}, { withCredentials: true });
                        if (!res.data.job_id) throw new Error("Failed to start regeneration");

                        let attempts = 0;
                        setJobProgress({ progress: 5, message: "Regenerating images..." });
                        while (attempts < 60) {
                          await new Promise(resolve => setTimeout(resolve, 5000));
                          try {
                            const statusRes = await axios.get(
                              `${API}/projects/${projectId}/ugc-multi-image-job-status?t=${Date.now()}`,
                              { headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate' }, withCredentials: true }
                            );
                            const job = statusRes.data;
                            if (job.status === "completed") {
                              setUgcMultiImages(job.result?.images || []);
                              setUgcMultiSelected([]);
                              toast.success("Images regenerated!");
                              setJobProgress(null);
                              return;
                            } else if (job.status === "failed") {
                              setJobProgress(null);
                              toast.error("Failed to regenerate");
                              return;
                            } else {
                              setJobProgress({ progress: job.progress || 10, message: job.message || "Regenerating..." });
                            }
                          } catch (pollError) {
                            if (pollError.message && !pollError.message.includes("Network Error")) { setJobProgress(null); throw pollError; }
                          }
                          attempts++;
                        }
                        setJobProgress(null);
                        toast.error("Regeneration timed out.");
                      } catch (e) { toast.error("Failed to regenerate"); } finally { setProcessing(false); setJobProgress(null); }
                    }} disabled={processing || !!jobProgress} data-testid="regenerate-ugc-multi-btn">
                      {processing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}Regenerate
                    </Button>
                    <Button onClick={() => setCurrentStep(6)} className="px-8 rounded-full bg-primary" data-testid="proceed-to-select-btn">
                      Proceed to Selection <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Step 6: Select 4 of 8 Images */}
          {currentStep === 6 && (videoType === "ugc_multi_video" || videoType === "ugc_broll_video") && (
            <motion.div key="ugc-multi-step6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-primary" strokeWidth={1.5} />
                </div>
                <h2 className="font-display text-2xl font-semibold mb-2">Select 4 Images</h2>
                <p className="text-muted-foreground text-sm">4 shots feature your product, 4 are avatar-only. Pick any 4 for your video scenes ({ugcMultiSelected.length}/4 selected)</p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {ugcMultiImages.map((img, i) => {
                  const isSelected = ugcMultiSelected.includes(i);
                  return (
                    <div
                      key={i}
                      onClick={() => {
                        if (isSelected) {
                          setUgcMultiSelected(prev => prev.filter(x => x !== i));
                        } else if (ugcMultiSelected.length < 4) {
                          setUgcMultiSelected(prev => [...prev, i]);
                        } else {
                          toast.error("You can only select 4 images");
                        }
                      }}
                      className={`aspect-[9/16] rounded-xl overflow-hidden cursor-pointer border-4 transition-all relative ${
                        isSelected ? 'border-primary shadow-lg' : 'border-transparent hover:border-primary/30'
                      }`}
                      data-testid={`ugc-multi-select-${i}`}
                    >
                      {img.image_path ? (
                        <img src={`${img.image_path}?t=${Date.now()}`} alt={`Angle ${i+1}`} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-muted"><p className="text-xs text-muted-foreground">Failed</p></div>
                      )}
                      <div
                        data-testid={`ugc-multi-badge-${i}`}
                        className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          img.has_product === false ? 'bg-neutral-800/80 text-neutral-200' : 'bg-primary/90 text-white'
                        }`}
                      >
                        {img.has_product === false ? 'No Product' : 'With Product'}
                      </div>
                      {isSelected && (
                        <div className="absolute top-2 right-2 w-7 h-7 bg-primary rounded-full flex items-center justify-center text-white text-xs font-bold">
                          {ugcMultiSelected.indexOf(i) + 1}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-center">
                <Button
                  onClick={async () => {
                    try {
                      setLoading(true);
                      await axios.post(`${API}/projects/${projectId}/ugc-multi/select-images`, { selected_indices: ugcMultiSelected }, { withCredentials: true });
                      toast.success("Images selected!");
                      setCurrentStep(7);
                    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save selection"); } finally { setLoading(false); }
                  }}
                  disabled={ugcMultiSelected.length !== 4 || loading}
                  className="px-8 h-12 rounded-full bg-primary hover:opacity-90 font-medium gap-2"
                  data-testid="confirm-selection-btn"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
                  Confirm Selection ({ugcMultiSelected.length}/4)
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 6: Select 1 Image — Story UGC */}
          {currentStep === 6 && videoType === "ugc_sora2_story" && (
            <motion.div key="ugc-story-step6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-primary" strokeWidth={1.5} />
                </div>
                <h2 className="font-display text-2xl font-semibold mb-2">Select 1 Reference Image</h2>
                <p className="text-muted-foreground text-sm">Choose the single best image — Sora 2 will use it as the visual reference for all 4 story scenes.</p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {ugcMultiImages.map((img, i) => {
                  const isSelected = ugcMultiSelected.length === 1 && ugcMultiSelected[0] === i;
                  return (
                    <div
                      key={i}
                      onClick={() => setUgcMultiSelected([i])}
                      className={`aspect-[9/16] rounded-xl overflow-hidden cursor-pointer border-4 transition-all relative ${
                        isSelected ? 'border-primary shadow-lg' : 'border-transparent hover:border-primary/30'
                      }`}
                      data-testid={`ugc-story-select-${i}`}
                    >
                      {img.image_path ? (
                        <img src={`${img.image_path}?t=${Date.now()}`} alt={`Candidate ${i+1}`} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-muted"><p className="text-xs text-muted-foreground">Failed</p></div>
                      )}
                      {isSelected && (
                        <div className="absolute top-2 right-2 w-7 h-7 bg-primary rounded-full flex items-center justify-center text-white">
                          <Check className="w-4 h-4" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-center">
                <Button
                  onClick={async () => {
                    try {
                      setLoading(true);
                      await axios.post(`${API}/projects/${projectId}/ugc-story/select-image`, { selected_index: ugcMultiSelected[0] }, { withCredentials: true });
                      toast.success("Reference image selected!");
                      setCurrentStep(7);
                    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save selection"); } finally { setLoading(false); }
                  }}
                  disabled={ugcMultiSelected.length !== 1 || loading}
                  className="px-8 h-12 rounded-full bg-primary hover:opacity-90 font-medium gap-2"
                  data-testid="confirm-story-selection-btn"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
                  Continue
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 7: Generate & Edit Scripts */}
          {currentStep === 7 && (videoType === "ugc_multi_video" || videoType === "ugc_broll_video" || videoType === "ugc_sora2_story") && (
            <motion.div key="ugc-multi-step7" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-primary" strokeWidth={1.5} />
                </div>
                <h2 className="font-display text-2xl font-semibold mb-2">Scene Scripts</h2>
                <p className="text-muted-foreground text-sm">{isUgcStoryType ? "4-scene story arc: Problem → Discovery → Transformation → CTA (~14-17 words each)" : "Generate and edit scripts for each scene (6 seconds / ~18 words each)"}</p>
              </div>

              {/* Voice Tone Selector */}
              <div className="max-w-sm mx-auto">
                <label className="text-sm font-medium mb-2 block">Voice Tone & Accent</label>
                <select
                  value={voiceTone}
                  onChange={(e) => setVoiceTone(e.target.value)}
                  className="w-full h-10 px-3 rounded-lg border border-input bg-background text-sm"
                  data-testid="voice-tone-select"
                >
                  {VOICE_TONES.map(tone => (
                    <option key={tone} value={tone}>{tone}</option>
                  ))}
                </select>
              </div>

              {/* Generate Scripts Button */}
              {ugcMultiScripts.every(s => !s) && (
                <div className="text-center">
                  <Button
                    onClick={async () => {
                      try {
                        setLoading(true);
                        toast.info("Generating scripts...");
                        const ep = isUgcStoryType ? "ugc-story" : "ugc-multi";
                        const res = await axios.post(`${API}/projects/${projectId}/${ep}/generate-scripts`, { voice_tone: voiceTone }, { withCredentials: true });
                        setUgcMultiScripts(res.data.scripts || ["", "", "", ""]);
                        toast.success("Scripts generated!");
                      } catch (e) { toast.error("Failed to generate scripts"); } finally { setLoading(false); }
                    }}
                    disabled={loading}
                    className="px-8 h-12 rounded-full bg-primary hover:opacity-90 font-medium gap-2"
                    data-testid="generate-multi-scripts-btn"
                  >
                    {loading ? <><Loader2 className="w-5 h-5 animate-spin" />Generating...</> : <><Sparkles className="w-5 h-5" />Generate Scripts</>}
                  </Button>
                </div>
              )}

              {/* Editable Scripts */}
              {ugcMultiScripts.some(s => s) && (
                <div className="space-y-4 max-w-2xl mx-auto">
                  {ugcMultiScripts.map((script, i) => (
                    <div key={i} className="space-y-1">
                      <label className="text-sm font-medium">Scene {i + 1}</label>
                      <textarea
                        value={script}
                        onChange={(e) => {
                          const updated = [...ugcMultiScripts];
                          updated[i] = e.target.value;
                          setUgcMultiScripts(updated);
                        }}
                        rows={2}
                        className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm resize-none"
                        data-testid={`multi-script-${i}`}
                      />
                      <p className="text-xs text-muted-foreground">{script.trim().split(/\s+/).filter(w => w).length}/18 words</p>
                    </div>
                  ))}

                  <div className="flex justify-center gap-3 pt-2">
                    <Button variant="outline" onClick={async () => {
                      try {
                        setLoading(true);
                        const ep = isUgcStoryType ? "ugc-story" : "ugc-multi";
                        const res = await axios.post(`${API}/projects/${projectId}/${ep}/generate-scripts`, { voice_tone: voiceTone }, { withCredentials: true });
                        setUgcMultiScripts(res.data.scripts || ["", "", "", ""]);
                        toast.success("Scripts regenerated!");
                      } catch (e) { toast.error("Failed to regenerate"); } finally { setLoading(false); }
                    }} disabled={loading} data-testid="regen-multi-scripts-btn">
                      <RefreshCw className="w-4 h-4 mr-1" />Regenerate
                    </Button>
                    <Button onClick={async () => {
                      try {
                        setLoading(true);
                        const ep = isUgcStoryType ? "ugc-story" : "ugc-multi";
                        await axios.put(`${API}/projects/${projectId}/${ep}/update-scripts`, { scripts: ugcMultiScripts }, { withCredentials: true });
                        toast.success("Scripts saved!");
                        setCurrentStep(8);
                      } catch (e) { toast.error("Failed to save scripts"); } finally { setLoading(false); }
                    }} disabled={loading || ugcMultiScripts.some(s => !s.trim())} className="px-8 rounded-full bg-primary" data-testid="save-scripts-proceed-btn">
                      {isUgcStoryType ? <>Save & Generate Video <ArrowRight className="w-4 h-4 ml-1" /></> : <>Save & Generate Videos <ArrowRight className="w-4 h-4 ml-1" /></>}
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Step 8: Generate Video — Story UGC (Sora 2 multi-scene) */}
          {currentStep === 8 && videoType === "ugc_sora2_story" && (
            <motion.div key="ugc-story-step8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Wand2 className="w-8 h-8 text-primary" strokeWidth={1.5} />
                </div>
                <h2 className="font-display text-2xl font-semibold mb-2">Generate Story Video</h2>
                <p className="text-muted-foreground text-sm">Single Sora 2 call with multi-scene story prompt (4 cuts, 20 seconds total)</p>
              </div>

              {jobProgress && (
                <div className="max-w-md mx-auto space-y-3">
                  <div className="h-3 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${jobProgress.progress}%` }} />
                  </div>
                  <p className="text-sm text-center text-muted-foreground">{jobProgress.message}</p>
                </div>
              )}

              {project?.ugc_story_final_video ? (
                <div className="max-w-md mx-auto space-y-4">
                  <video src={`${project.ugc_story_final_video}?t=${Date.now()}`} controls className="w-full rounded-lg aspect-[9/16] object-cover" data-testid="story-video-preview" />
                  <div className="text-center">
                    <Button onClick={() => setCurrentStep(9)} className="px-8 h-12 rounded-full bg-primary hover:opacity-90 font-medium gap-2" data-testid="story-proceed-final-btn">
                      Continue <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Button
                    onClick={async () => {
                      try {
                        setProcessing(true);
                        toast.info("Starting Story video generation with Sora 2...");
                        const res = await axios.post(`${API}/projects/${projectId}/ugc-story/generate-video`, {}, { withCredentials: true });
                        if (!res.data.job_id) throw new Error("Failed to start video generation");
                        toast.info("Sora 2 is generating your story video. This typically takes 2-5 minutes.");

                        let attempts = 0;
                        const maxAttempts = 90; // up to ~7.5 min
                        setJobProgress({ progress: 5, message: "Starting Sora 2 generation..." });

                        while (attempts < maxAttempts) {
                          await new Promise(resolve => setTimeout(resolve, 5000));
                          try {
                            const statusRes = await axios.get(
                              `${API}/projects/${projectId}/ugc-story-video-job-status?t=${Date.now()}`,
                              { headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate', 'Pragma': 'no-cache' }, withCredentials: true }
                            );
                            const job = statusRes.data;
                            if (job.status === "completed") {
                              setJobProgress({ progress: 100, message: "Video ready!" });
                              toast.success("Story video generated!");
                              fireAdGenerated();
                              await fetchProject();
                              setJobProgress(null);
                              break;
                            }
                            if (job.status === "failed") {
                              throw new Error(job.error || "Generation failed");
                            }
                            setJobProgress({ progress: job.progress || 10, message: job.message || "Generating..." });
                          } catch (statusErr) {
                            // Continue polling on transient errors
                          }
                          attempts++;
                        }

                        if (attempts >= maxAttempts) {
                          setJobProgress(null);
                          toast.error("Generation timed out. Please try again.");
                        }
                      } catch (e) {
                        setJobProgress(null);
                        toast.error(e.response?.data?.detail || e.message || "Failed to generate video");
                      } finally {
                        setProcessing(false);
                      }
                    }}
                    disabled={processing || !!jobProgress}
                    className="px-8 h-12 rounded-full bg-primary hover:opacity-90 font-medium gap-2"
                    data-testid="generate-story-video-btn"
                  >
                    {processing ? <><Loader2 className="w-5 h-5 animate-spin" />Generating...</> : <><Film className="w-5 h-5" />Generate Story Video</>}
                  </Button>
                </div>
              )}
            </motion.div>
          )}

          {/* Step 8: Generate Videos */}
          {currentStep === 8 && (videoType === "ugc_multi_video" || videoType === "ugc_broll_video") && (
            <motion.div key="ugc-multi-step8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Wand2 className="w-8 h-8 text-primary" strokeWidth={1.5} />
                </div>
                <h2 className="font-display text-2xl font-semibold mb-2">Generate Videos</h2>
                <p className="text-muted-foreground text-sm">Generate 4 video scenes using Veo 3.1 (6s each)</p>
              </div>

              {jobProgress && (
                <div className="max-w-md mx-auto space-y-3">
                  <div className="h-3 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${jobProgress.progress}%` }} />
                  </div>
                  <p className="text-sm text-center text-muted-foreground">{jobProgress.message}</p>
                </div>
              )}

              {/* Show scene previews after generation */}
              {(project?.video_scenes || []).some(s => s.video_path || s.video_url) ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {(project?.video_scenes || []).map((scene, i) => {
                      const key = `scene-${i}`;
                      const isOpen = retryOpenKey === key;
                      const isBusy = retryingKey === key;
                      const stitched = !!project?.final_video_url;
                      return (
                        <div key={i} className="space-y-2">
                          <p className="text-xs font-medium text-center">Scene {i+1}</p>
                          {(scene.video_path || scene.video_url) ? (
                            <video src={`${scene.video_path || scene.video_url}?t=${Date.now()}`} controls className="w-full rounded-lg aspect-[9/16] object-cover" data-testid={`multi-scene-video-${i}`} />
                          ) : (
                            <div className="w-full aspect-[9/16] rounded-lg bg-muted flex items-center justify-center"><p className="text-xs text-muted-foreground">Not available</p></div>
                          )}
                          {(scene.video_path || scene.video_url) && !stitched && (
                            <>
                              {!isOpen ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openRetryEditor(key, scene.prompt || scene.veo_prompt || scene.kling_prompt || "")}
                                  disabled={isBusy || !!jobProgress || videoType === 'ugc_broll_video'}
                                  className="w-full text-xs gap-1"
                                  data-testid={`retry-scene-${i}-btn`}
                                >
                                  <RefreshCw className="w-3 h-3" />Retry ({videoEditCost} CR)
                                </Button>
                              ) : (
                                <div className="space-y-1.5 p-2 rounded-lg bg-muted/40 border border-primary/20">
                                  <Textarea
                                    value={retryPromptDraft}
                                    onChange={(e) => setRetryPromptDraft(e.target.value)}
                                    className="text-xs min-h-[80px] resize-none"
                                    placeholder="Prompt for this scene..."
                                    data-testid={`retry-scene-${i}-textarea`}
                                  />
                                  <div className="flex gap-1.5">
                                    <Button size="sm" variant="ghost" onClick={closeRetryEditor} disabled={isBusy} className="flex-1 text-xs">Cancel</Button>
                                    <Button size="sm" onClick={() => retryUgcMultiScene(i)} disabled={isBusy || !retryPromptDraft.trim()} className="flex-1 text-xs bg-primary gap-1" data-testid={`retry-scene-${i}-confirm`}>
                                      {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><RefreshCw className="w-3 h-3" />{videoEditCost} CR</>}
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-center gap-3 flex-wrap">
                    {videoType === "ugc_multi_video" && !project?.final_video_url && (
                      <Button
                        variant="outline"
                        onClick={async () => {
                          try {
                            setProcessing(true);
                            toast.info("Regenerating all 4 videos...");
                            const res = await axios.post(`${API}/projects/${projectId}/ugc-multi/generate-videos`, {}, { withCredentials: true });
                            if (!res.data.job_id) throw new Error("Failed to start");
                            await pollJobStatus("ugc_multi_video_gen", async () => { toast.success("All 4 videos regenerated!"); refreshCredits(); await fetchProject(); });
                          } catch (e) { toast.error(e.response?.data?.detail || e.message || "Failed to regenerate"); }
                          finally { setProcessing(false); }
                        }}
                        disabled={processing || !!jobProgress || !!retryingKey}
                        className="rounded-full gap-1"
                        data-testid="regenerate-all-multi-videos-btn"
                      >
                        <RefreshCw className="w-4 h-4" />Regenerate All 4
                      </Button>
                    )}
                    <Button onClick={() => setCurrentStep(9)} className="px-8 h-12 rounded-full bg-primary hover:opacity-90 font-medium gap-2" data-testid="proceed-to-final-btn">
                      Create Final Video <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Button
                    onClick={async () => {
                      try {
                        setProcessing(true);
                        toast.info("Starting video generation...");
                        const res = await axios.post(`${API}/projects/${projectId}/ugc-multi/generate-videos`, {}, { withCredentials: true });
                        if (!res.data.job_id) throw new Error("Failed to start video generation");
                        toast.info("Video generation in progress. This may take a few minutes.");
                        await pollJobStatus("ugc_multi_video_gen", async () => {
                          toast.success("All 4 videos generated!");
                          await fetchProject();
                        });
                      } catch (e) {
                        toast.error(e.response?.data?.detail || e.message || "Failed to generate videos");
                      } finally {
                        setProcessing(false);
                      }
                    }}
                    disabled={processing || !!jobProgress}
                    className="px-8 h-12 rounded-full bg-primary hover:opacity-90 font-medium gap-2"
                    data-testid="generate-multi-videos-btn"
                  >
                    {processing ? <><Loader2 className="w-5 h-5 animate-spin" />Generating...</> : <><Film className="w-5 h-5" />Generate 4 Videos</>}
                  </Button>
                </div>
              )}
            </motion.div>
          )}

          {/* Step 9: Final - Stitch & Download */}
          {currentStep === 9 && (videoType === "ugc_multi_video" || videoType === "ugc_sora2_story") && (
            <motion.div key="ugc-multi-step9" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Download className="w-8 h-8 text-primary" strokeWidth={1.5} />
                </div>
                <h2 className="font-display text-2xl font-semibold mb-2">{project?.final_video_url ? "Your Video is Ready" : "Create Final Video"}</h2>
                <p className="text-muted-foreground text-sm">{project?.final_video_url ? "Download your stitched UGC video" : "Stitch your 4 scenes into one final video"}</p>
              </div>

              {/* Job progress for stitching */}
              {jobProgress && (
                <div className="max-w-md mx-auto space-y-3">
                  <div className="h-3 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${jobProgress.progress}%` }} />
                  </div>
                  <p className="text-sm text-center text-muted-foreground">{jobProgress.message}</p>
                </div>
              )}

              {/* Stitch button */}
              {!project?.final_video_url && !jobProgress && (
                <div className="text-center py-8">
                  <Button
                    onClick={async () => {
                      try {
                        setProcessing(true);
                        toast.info("Stitching scenes together...");
                        await axios.post(`${API}/projects/${projectId}/stitch`, { audio_type: "music" }, { withCredentials: true });
                        await pollJobStatus("stitch_video", async () => {
                          toast.success("Final video created!");
                          await fetchProject();
                          setCurrentStep(9);
                        });
                      } catch (e) {
                        toast.error(e.message || "Failed to stitch video");
                      } finally {
                        setProcessing(false);
                      }
                    }}
                    disabled={processing}
                    className="px-8 h-12 rounded-full bg-primary hover:opacity-90 font-medium gap-2"
                    data-testid="stitch-multi-btn"
                  >
                    {processing ? <><Loader2 className="w-5 h-5 animate-spin" />Stitching...</> : <><Film className="w-5 h-5" />Stitch Final Video</>}
                  </Button>
                </div>
              )}

              {/* Final video + download + subtitles */}
              {project?.final_video_url && (
                <div className="space-y-8">
                  {/* Side-by-side video display */}
                  <div className={`grid ${project?.final_video_subtitled_scribe_url ? 'grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl' : 'grid-cols-1 max-w-sm'} mx-auto`}>
                    {/* Original Video */}
                    <div className="space-y-3">
                      <h3 className="text-sm font-semibold text-center">Original Video</h3>
                      <div className="rounded-xl overflow-hidden border border-border">
                        <video src={`${project.final_video_url}?t=${Date.now()}`} controls className="w-full" data-testid="multi-final-video" />
                      </div>
                      <div className="flex justify-center">
                        <Button
                          size="sm"
                          className="rounded-full bg-primary hover:opacity-90 font-medium gap-2"
                          data-testid="download-multi-video-btn"
                          onClick={() => {
                            const a = document.createElement("a");
                            a.href = `${API}/download-video?url=${encodeURIComponent(project.final_video_url)}`;
                            a.download = "ugc-multi-scene.mp4";
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                          }}
                        >
                          <Download className="w-4 h-4" />Download
                        </Button>
                      </div>
                    </div>

                    {/* Subtitled Video (if generated) */}
                    {project?.final_video_subtitled_scribe_url && (
                      <div className="space-y-3">
                        <h3 className="text-sm font-semibold text-center">With Subtitles</h3>
                        <div className="rounded-xl overflow-hidden border border-border">
                          <video src={`${project.final_video_subtitled_scribe_url}?t=${Date.now()}`} controls className="w-full" data-testid="multi-subtitled-video" />
                        </div>
                        <div className="flex justify-center">
                          <Button
                            size="sm"
                            className="rounded-full bg-primary hover:opacity-90 font-medium gap-2"
                            data-testid="download-subtitled-video-btn"
                            onClick={() => {
                              const a = document.createElement("a");
                              a.href = `${API}/download-video?url=${encodeURIComponent(project.final_video_subtitled_scribe_url)}`;
                              a.download = "ugc-multi-scene-subtitled.mp4";
                              document.body.appendChild(a);
                              a.click();
                              document.body.removeChild(a);
                            }}
                          >
                            <Download className="w-4 h-4" />Download
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Add Subtitles section */}
                  <div className="max-w-2xl mx-auto border-t border-border pt-6">
                    <h3 className="text-sm font-semibold text-center mb-4">Add Subtitles</h3>

                    {/* Style selector cards */}
                    <div className="grid grid-cols-2 gap-3 max-w-md mx-auto mb-5">
                      <button
                        onClick={() => setSubtitleStyle("bold_pop")}
                        className={`relative p-4 rounded-xl border-2 transition-all text-center ${subtitleStyle === "bold_pop" ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
                        data-testid="style-bold-pop"
                      >
                        {subtitleStyle === "bold_pop" && <div className="absolute top-2 right-2"><Check className="w-4 h-4 text-primary" /></div>}
                        <div className="bg-black rounded-lg p-3 mb-2 aspect-video flex items-center justify-center">
                          <span className="text-white font-black text-lg tracking-wide">CHANGED</span>
                        </div>
                        <p className="text-xs font-semibold">Bold Pop</p>
                        <p className="text-[10px] text-muted-foreground">Large, 1-2 words at a time</p>
                      </button>
                      <button
                        onClick={() => setSubtitleStyle("karaoke")}
                        className={`relative p-4 rounded-xl border-2 transition-all text-center ${subtitleStyle === "karaoke" ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
                        data-testid="style-karaoke"
                      >
                        {subtitleStyle === "karaoke" && <div className="absolute top-2 right-2"><Check className="w-4 h-4 text-primary" /></div>}
                        <div className="bg-black rounded-lg p-3 mb-2 aspect-video flex items-center justify-center">
                          <span className="text-white/60 text-sm font-bold">This </span>
                          <span className="text-yellow-400 text-sm font-bold">ONE </span>
                          <span className="text-white/60 text-sm font-bold">changed</span>
                        </div>
                        <p className="text-xs font-semibold">Karaoke</p>
                        <p className="text-[10px] text-muted-foreground">Word-by-word highlight</p>
                      </button>
                    </div>

                    {/* Subtitle job progress */}
                    {jobProgress && (
                      <div className="max-w-md mx-auto space-y-3 mb-4">
                        <div className="h-3 bg-muted rounded-full overflow-hidden">
                          <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${jobProgress.progress}%` }} />
                        </div>
                        <p className="text-sm text-center text-muted-foreground">{jobProgress.message}</p>
                      </div>
                    )}

                    <div className="flex justify-center">
                      <Button
                        variant="outline"
                        className="rounded-full gap-2"
                        data-testid="subtitle-scribe-btn"
                        disabled={processing || !!jobProgress}
                        onClick={async () => {
                          try {
                            setProcessing(true);
                            toast.info("Generating subtitles...");
                            const res = await axios.post(`${API}/projects/${projectId}/add-subtitles`, { method: "scribe", style: subtitleStyle }, { withCredentials: true });
                            if (!res.data.job_id) throw new Error("Failed to start subtitle generation");

                            let attempts = 0;
                            setJobProgress({ progress: 5, message: "Transcribing audio with AI..." });
                            while (attempts < 60) {
                              await new Promise(resolve => setTimeout(resolve, 5000));
                              try {
                                const statusRes = await axios.get(`${API}/projects/${projectId}/subtitle-job-status?t=${Date.now()}`, { withCredentials: true });
                                const job = statusRes.data;
                                if (job.status === "completed") {
                                  toast.success("Subtitles added!");
                                  setJobProgress(null);
                                  await fetchProject();
                                  setCurrentStep(9);
                                  return;
                                } else if (job.status === "failed") {
                                  setJobProgress(null);
                                  toast.error(job.error || "Subtitle generation failed");
                                  return;
                                } else {
                                  setJobProgress({ progress: job.progress || 10, message: job.message || "Processing..." });
                                }
                              } catch (pollErr) {
                                if (!pollErr.message?.includes("Network Error")) { setJobProgress(null); throw pollErr; }
                              }
                              attempts++;
                            }
                            setJobProgress(null);
                            toast.error("Subtitle generation timed out.");
                          } catch (e) { toast.error(e.response?.data?.detail || e.message || "Failed"); } finally { setProcessing(false); setJobProgress(null); }
                        }}
                      >
                        <Sparkles className="w-4 h-4" />Add Subtitles
                      </Button>
                    </div>
                  </div>

                  {/* Back to projects */}
                  <div className="flex justify-center">
                    <Button variant="outline" onClick={() => navigate('/projects')} className="px-8 h-12 rounded-full">
                      Back to Projects
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Step 9: B-Roll Generation (ugc_broll_video only) */}
          {currentStep === 9 && videoType === "ugc_broll_video" && (
            <motion.div key="ugc-broll-step9" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Clapperboard className="w-8 h-8 text-primary" strokeWidth={1.5} />
                </div>
                <h2 className="font-display text-2xl font-semibold mb-2">Generate B-Roll</h2>
                <p className="text-muted-foreground text-sm">Generate 4 B-roll cutaway clips to interleave with your UGC scenes</p>
              </div>

              {jobProgress && (
                <div className="max-w-md mx-auto space-y-3">
                  <div className="h-3 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${jobProgress.progress}%` }} />
                  </div>
                  <p className="text-sm text-center text-muted-foreground">{jobProgress.message}</p>
                </div>
              )}

              {/* Show B-roll previews if generated */}
              {(project?.broll_clips || []).length > 0 ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {(project?.broll_clips || []).map((clip, i) => {
                      const key = `broll-${i}`;
                      const isOpen = retryOpenKey === key;
                      const isBusy = retryingKey === key;
                      const stitched = !!project?.final_video_url;
                      return (
                        <div key={i} className="space-y-2">
                          <p className="text-xs font-medium text-center">B-Roll {i+1}</p>
                          {clip.video_path ? (
                            <video src={`${clip.video_path}?t=${Date.now()}`} controls className="w-full rounded-lg aspect-[9/16] object-cover" data-testid={`broll-clip-${i}`} />
                          ) : (
                            <div className="w-full aspect-[9/16] rounded-lg bg-muted flex items-center justify-center"><p className="text-xs text-muted-foreground">Not available</p></div>
                          )}
                          {clip.prompt && !isOpen && <p className="text-[10px] text-muted-foreground text-center line-clamp-2">{clip.prompt}</p>}
                          {clip.video_path && !stitched && (
                            <>
                              {!isOpen ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openRetryEditor(key, clip.prompt || "")}
                                  disabled={isBusy || !!jobProgress}
                                  className="w-full text-xs gap-1"
                                  data-testid={`retry-broll-${i}-btn`}
                                >
                                  <RefreshCw className="w-3 h-3" />Retry ({videoEditCost} CR)
                                </Button>
                              ) : (
                                <div className="space-y-1.5 p-2 rounded-lg bg-muted/40 border border-primary/20">
                                  <Textarea
                                    value={retryPromptDraft}
                                    onChange={(e) => setRetryPromptDraft(e.target.value)}
                                    className="text-xs min-h-[80px] resize-none"
                                    placeholder="Prompt for this B-roll clip..."
                                    data-testid={`retry-broll-${i}-textarea`}
                                  />
                                  <div className="flex gap-1.5">
                                    <Button size="sm" variant="ghost" onClick={closeRetryEditor} disabled={isBusy} className="flex-1 text-xs">Cancel</Button>
                                    <Button size="sm" onClick={() => retryBrollClip(i)} disabled={isBusy || !retryPromptDraft.trim()} className="flex-1 text-xs bg-primary gap-1" data-testid={`retry-broll-${i}-confirm`}>
                                      {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><RefreshCw className="w-3 h-3" />{videoEditCost} CR</>}
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-center gap-3">
                    <Button variant="outline" onClick={async () => {
                      try {
                        setProcessing(true);
                        toast.info("Regenerating B-roll clips...");
                        const res = await axios.post(`${API}/projects/${projectId}/ugc-broll/generate-clips`, {}, { withCredentials: true });
                        if (!res.data.job_id) throw new Error("Failed");
                        setJobProgress({ progress: 5, message: "Generating B-roll clips..." });
                        await pollJobStatus("broll_generation", async () => { toast.success("B-roll regenerated!"); await fetchProject(); setCurrentStep(9); });
                      } catch (e) { toast.error(e.response?.data?.detail || e.message || "Failed"); } finally { setProcessing(false); setJobProgress(null); }
                    }} disabled={processing || !!jobProgress || !!retryingKey || !!project?.final_video_url} data-testid="regenerate-broll-btn">
                      <RefreshCw className="w-4 h-4 mr-1" />Regenerate All
                    </Button>
                    <Button onClick={() => setCurrentStep(10)} className="px-8 rounded-full bg-primary" data-testid="proceed-to-broll-final-btn">
                      Create Final Video <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Button
                    onClick={async () => {
                      try {
                        setProcessing(true);
                        toast.info("Generating B-roll prompts & clips...");
                        const res = await axios.post(`${API}/projects/${projectId}/ugc-broll/generate-clips`, {}, { withCredentials: true });
                        if (!res.data.job_id) throw new Error("Failed to start B-roll generation");
                        toast.info("Generating 4 B-roll clips... This may take a few minutes.");
                        setJobProgress({ progress: 5, message: "Generating B-roll prompts..." });
                        await pollJobStatus("broll_generation", async () => {
                          toast.success("B-roll clips generated!");
                          await fetchProject();
                          setCurrentStep(9);
                        });
                      } catch (e) {
                        toast.error(e.response?.data?.detail || e.message || "Failed to generate B-roll");
                      } finally {
                        setProcessing(false);
                        setJobProgress(null);
                      }
                    }}
                    disabled={processing || !!jobProgress}
                    className="px-8 h-12 rounded-full bg-primary hover:opacity-90 font-medium gap-2"
                    data-testid="generate-broll-btn"
                  >
                    {processing ? <><Loader2 className="w-5 h-5 animate-spin" />Generating B-Roll...</> : <><Clapperboard className="w-5 h-5" />Generate 4 B-Roll Clips</>}
                  </Button>
                </div>
              )}
            </motion.div>
          )}

          {/* Step 10: Final - Stitch with B-Roll & Download (ugc_broll_video only) */}
          {currentStep === 10 && videoType === "ugc_broll_video" && (
            <motion.div key="ugc-broll-step10" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Download className="w-8 h-8 text-primary" strokeWidth={1.5} />
                </div>
                <h2 className="font-display text-2xl font-semibold mb-2">{project?.final_video_url ? "Your Video is Ready" : "Create Final Video"}</h2>
                <p className="text-muted-foreground text-sm">{project?.final_video_url ? "Download your UGC video with B-roll cutaways" : "Stitch scenes with B-roll overlays"}</p>
              </div>

              {jobProgress && (
                <div className="max-w-md mx-auto space-y-3">
                  <div className="h-3 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${jobProgress.progress}%` }} />
                  </div>
                  <p className="text-sm text-center text-muted-foreground">{jobProgress.message}</p>
                </div>
              )}

              {/* Stitch button */}
              {!project?.final_video_url && !jobProgress && (
                <div className="text-center py-8">
                  <Button
                    onClick={async () => {
                      try {
                        setProcessing(true);
                        toast.info("Stitching scenes with B-roll overlays...");
                        await axios.post(`${API}/projects/${projectId}/ugc-broll/stitch`, {}, { withCredentials: true });
                        await pollJobStatus("broll_stitch", async () => {
                          toast.success("Final video created!");
                          await fetchProject();
                          setCurrentStep(10);
                        });
                      } catch (e) {
                        toast.error(e.message || "Failed to stitch video");
                      } finally {
                        setProcessing(false);
                      }
                    }}
                    disabled={processing}
                    className="px-8 h-12 rounded-full bg-primary hover:opacity-90 font-medium gap-2"
                    data-testid="stitch-broll-btn"
                  >
                    {processing ? <><Loader2 className="w-5 h-5 animate-spin" />Stitching...</> : <><Film className="w-5 h-5" />Stitch Final Video</>}
                  </Button>
                </div>
              )}

              {/* Final video + subtitles */}
              {project?.final_video_url && (
                <div className="space-y-8">
                  {/* Re-stitch button */}
                  <div className="text-center">
                    <Button
                      variant="outline"
                      onClick={async () => {
                        try {
                          setProcessing(true);
                          toast.info("Re-stitching scenes with B-roll overlays...");
                          await axios.post(`${API}/projects/${projectId}/ugc-broll/stitch`, {}, { withCredentials: true });
                          await pollJobStatus("broll_stitch", async () => {
                            toast.success("Final video re-stitched!");
                            await fetchProject();
                            setCurrentStep(10);
                          });
                        } catch (e) {
                          toast.error(e.message || "Failed to re-stitch video");
                        } finally {
                          setProcessing(false);
                        }
                      }}
                      disabled={processing}
                      className="rounded-full font-medium gap-2"
                      data-testid="restitch-broll-btn"
                    >
                      {processing ? <><Loader2 className="w-5 h-5 animate-spin" />Re-stitching...</> : <><RefreshCw className="w-4 h-4" />Re-stitch Video</>}
                    </Button>
                  </div>

                  <div className={`grid ${project?.final_video_subtitled_scribe_url ? 'grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl' : 'grid-cols-1 max-w-sm'} mx-auto`}>
                    <div className="space-y-3">
                      <h3 className="text-sm font-semibold text-center">Original Video</h3>
                      <div className="rounded-xl overflow-hidden border border-border">
                        <video src={`${project.final_video_url}?t=${Date.now()}`} controls className="w-full" data-testid="broll-final-video" />
                      </div>
                      <div className="flex justify-center">
                        <Button size="sm" className="rounded-full bg-primary hover:opacity-90 font-medium gap-2" data-testid="download-broll-video-btn" onClick={() => {
                          const a = document.createElement("a"); a.href = `${API}/download-video?url=${encodeURIComponent(project.final_video_url)}`; a.download = "ugc-broll.mp4"; document.body.appendChild(a); a.click(); document.body.removeChild(a);
                        }}>
                          <Download className="w-4 h-4" />Download
                        </Button>
                      </div>
                    </div>
                    {project?.final_video_subtitled_scribe_url && (
                      <div className="space-y-3">
                        <h3 className="text-sm font-semibold text-center">With Subtitles</h3>
                        <div className="rounded-xl overflow-hidden border border-border">
                          <video src={`${project.final_video_subtitled_scribe_url}?t=${Date.now()}`} controls className="w-full" data-testid="broll-subtitled-video" />
                        </div>
                        <div className="flex justify-center">
                          <Button size="sm" className="rounded-full bg-primary hover:opacity-90 font-medium gap-2" data-testid="download-broll-subtitled-btn" onClick={() => {
                            const a = document.createElement("a"); a.href = `${API}/download-video?url=${encodeURIComponent(project.final_video_subtitled_scribe_url)}`; a.download = "ugc-broll-subtitled.mp4"; document.body.appendChild(a); a.click(); document.body.removeChild(a);
                          }}>
                            <Download className="w-4 h-4" />Download
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Add Subtitles */}
                  <div className="max-w-2xl mx-auto border-t border-border pt-6">
                    <h3 className="text-sm font-semibold text-center mb-4">Add Subtitles</h3>
                    <div className="grid grid-cols-2 gap-3 max-w-md mx-auto mb-5">
                      <button onClick={() => setSubtitleStyle("bold_pop")} className={`relative p-4 rounded-xl border-2 transition-all text-center ${subtitleStyle === "bold_pop" ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`} data-testid="broll-style-bold-pop">
                        {subtitleStyle === "bold_pop" && <div className="absolute top-2 right-2"><Check className="w-4 h-4 text-primary" /></div>}
                        <div className="bg-black rounded-lg p-3 mb-2 aspect-video flex items-center justify-center"><span className="text-white font-black text-lg tracking-wide">CHANGED</span></div>
                        <p className="text-xs font-semibold">Bold Pop</p>
                        <p className="text-[10px] text-muted-foreground">Large, 1-2 words at a time</p>
                      </button>
                      <button onClick={() => setSubtitleStyle("karaoke")} className={`relative p-4 rounded-xl border-2 transition-all text-center ${subtitleStyle === "karaoke" ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`} data-testid="broll-style-karaoke">
                        {subtitleStyle === "karaoke" && <div className="absolute top-2 right-2"><Check className="w-4 h-4 text-primary" /></div>}
                        <div className="bg-black rounded-lg p-3 mb-2 aspect-video flex items-center justify-center"><span className="text-white/60 text-sm font-bold">This </span><span className="text-yellow-400 text-sm font-bold">ONE </span><span className="text-white/60 text-sm font-bold">changed</span></div>
                        <p className="text-xs font-semibold">Karaoke</p>
                        <p className="text-[10px] text-muted-foreground">Word-by-word highlight</p>
                      </button>
                    </div>
                    {jobProgress && (
                      <div className="max-w-md mx-auto space-y-3 mb-4">
                        <div className="h-3 bg-muted rounded-full overflow-hidden"><div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${jobProgress.progress}%` }} /></div>
                        <p className="text-sm text-center text-muted-foreground">{jobProgress.message}</p>
                      </div>
                    )}
                    <div className="flex justify-center">
                      <Button variant="outline" className="rounded-full gap-2" data-testid="broll-subtitle-btn" disabled={processing || !!jobProgress}
                        onClick={async () => {
                          try {
                            setProcessing(true); toast.info("Generating subtitles...");
                            const res = await axios.post(`${API}/projects/${projectId}/add-subtitles`, { method: "scribe", style: subtitleStyle }, { withCredentials: true });
                            if (!res.data.job_id) throw new Error("Failed");
                            let attempts = 0; setJobProgress({ progress: 5, message: "Transcribing audio with AI..." });
                            while (attempts < 60) {
                              await new Promise(resolve => setTimeout(resolve, 5000));
                              try {
                                const statusRes = await axios.get(`${API}/projects/${projectId}/subtitle-job-status?t=${Date.now()}`, { withCredentials: true });
                                const job = statusRes.data;
                                if (job.status === "completed") { toast.success("Subtitles added!"); setJobProgress(null); await fetchProject(); setCurrentStep(10); return; }
                                else if (job.status === "failed") { setJobProgress(null); toast.error(job.error || "Failed"); return; }
                                else { setJobProgress({ progress: job.progress || 10, message: job.message || "Processing..." }); }
                              } catch (pollErr) { if (!pollErr.message?.includes("Network Error")) { setJobProgress(null); throw pollErr; } }
                              attempts++;
                            }
                            setJobProgress(null); toast.error("Timed out.");
                          } catch (e) { toast.error(e.response?.data?.detail || e.message || "Failed"); } finally { setProcessing(false); setJobProgress(null); }
                        }}
                      >
                        <Sparkles className="w-4 h-4" />Add Subtitles
                      </Button>
                    </div>
                  </div>

                  <div className="flex justify-center">
                    <Button variant="outline" onClick={() => navigate('/projects')} className="px-8 h-12 rounded-full">Back to Projects</Button>
                  </div>
                </div>
              )}
            </motion.div>
          )}


          {/* Step 4: Image Upload (20s Ad, 10s Ad) */}
          {currentStep === 4 && (videoType === "20_second_ad" || videoType === "20_second_ad_v2" || videoType === "10_second_ad" || videoType === "24_second_ad_premium") && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Upload className="w-8 h-8 text-primary" strokeWidth={1.5} />
                </div>
                <h2 className="font-display text-2xl font-semibold mb-2">Upload Product Images</h2>
                <p className="text-muted-foreground">
                  Upload 1-{videoType === "10_second_ad" ? "2" : "4"} product images for your ad scenes
                </p>
              </div>

              <div className="max-w-xl mx-auto">
                <label
                  className="upload-zone flex flex-col items-center justify-center p-12 rounded-xl cursor-pointer hover:border-primary"
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const files = e.dataTransfer?.files;
                    if (files?.length) {
                      const syntheticEvent = { target: { files } };
                      handleFileUpload(syntheticEvent);
                    }
                  }}
                >
                  <Upload className="w-10 h-10 text-muted-foreground mb-4" strokeWidth={1.5} />
                  <p className="text-lg font-medium mb-1">Drop images here</p>
                  <p className="text-sm text-muted-foreground mb-2">or click to browse (max {videoType === "10_second_ad" ? "2" : "4"} images)</p>
                  <p className="text-xs text-muted-foreground">Supported formats: PNG, JPEG, WEBP, GIF</p>
                  <input
                    type="file"
                    accept=".png,.jpg,.jpeg,.webp,.gif"
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="image-upload-input"
                  />
                </label>

                {uploadPreviews.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                    {uploadPreviews.map((preview, i) => (
                      <div key={i} className="aspect-square rounded-lg overflow-hidden bg-secondary">
                        <img src={preview} alt={`Preview ${i + 1}`} className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                )}

                <Button
                  onClick={uploadImages}
                  disabled={processing || uploadedFiles.length === 0}
                  className="w-full h-14 rounded-full bg-primary hover:opacity-90 text-lg font-medium gap-2 mt-6"
                  data-testid="upload-images-btn"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Continue
                      <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 5: Script Review (V1/V2/10s only - V3 is automated) */}
          {(currentStep === 5 && (videoType === "20_second_ad" || videoType === "20_second_ad_v2" || videoType === "10_second_ad" || videoType === "24_second_ad_premium")) && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <FileText className="w-8 h-8 text-primary" strokeWidth={1.5} />
                </div>
                <h2 className="font-display text-2xl font-semibold mb-2">Review Your Script</h2>
                <p className="text-muted-foreground">4 scenes × 5 seconds = 20-second ad</p>
              </div>

              {project?.script && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                  {(videoType === "10_second_ad" ? ['scene_1', 'scene_2'] : ['scene_1', 'scene_2', 'scene_3', 'scene_4']).map((sceneKey, i) => {
                    const scene = project.script[sceneKey];
                    if (!scene) return null; // Skip null scenes
                    return (
                      <div key={sceneKey} className="scene-card rounded-xl p-6 border border-border">
                        <div className="flex items-center gap-2 mb-4">
                          <span className="font-mono text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                            {scene.duration}
                          </span>
                          <span className="font-display font-semibold">Scene {i + 1}</span>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Voiceover</p>
                            <p className="text-foreground font-medium">"{scene.voiceover}"</p>
                          </div>
                          {scene.on_screen_text && (
                            <div>
                              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">On-Screen Text</p>
                              <p className="text-sm bg-primary/5 px-2 py-1 rounded inline-block">{scene.on_screen_text}</p>
                            </div>
                          )}
                          <div>
                            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">What We See</p>
                            <p className="text-sm text-muted-foreground">{scene.visual_intent}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="flex justify-center">
                <Button
                  onClick={generateAnchorImages}
                  disabled={processing}
                  className="px-12 h-14 rounded-full bg-primary hover:opacity-90 text-lg font-medium gap-2"
                  data-testid="generate-anchor-images-btn"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Generating Images...
                    </>
                  ) : (
                    <>
                      Generate Anchor Images
                      <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 6: Anchor Images (V1/V2/10s only - V3 is automated) */}
          {(currentStep === 6 && (videoType === "20_second_ad" || videoType === "20_second_ad_v2" || videoType === "10_second_ad" || videoType === "24_second_ad_premium")) && (
            <motion.div
              key="step5"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <ImageIcon className="w-8 h-8 text-primary" strokeWidth={1.5} />
                </div>
                <h2 className="font-display text-2xl font-semibold mb-2">Anchor Images</h2>
                <p className="text-muted-foreground">AI-generated visuals for each scene</p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
                {project?.anchor_images?.map((anchor, i) => (
                  <div key={i} className="space-y-2">
                    <div className={`relative aspect-[9/16] rounded-xl overflow-hidden bg-secondary ${aspectRatio === '16:9' ? 'aspect-video' : ''}`}>
                      {anchor.generated_image_url ? (
                        <>
                          <img 
                            key={`img-${i}-${imageVersions[i] || 0}`}
                            src={`${API}/files/${anchor.generated_image_url.split('/').pop()}?v=${imageVersions[i] || Date.now()}`} 
                            alt={`Scene ${anchor.scene}`}
                            className="w-full h-full object-cover cursor-pointer"
                            onClick={() => setFullscreenImageIndex(i)}
                            data-testid={`anchor-image-${i}`}
                          />
                          {/* QC Badge */}
                          {anchor.qc_status && (
                            <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
                              anchor.qc_status === 'PASS' ? 'bg-green-500/90 text-white' :
                              anchor.qc_status === 'MANUAL_REVIEW' ? 'bg-yellow-500/90 text-white' :
                              anchor.qc_status === 'FAIL' ? 'bg-red-500/90 text-white' :
                              'bg-gray-500/90 text-white'
                            }`}>
                              {anchor.qc_status === 'PASS' && <Check className="w-3 h-3" />}
                              {anchor.qc_status === 'FAIL' && <X className="w-3 h-3" />}
                              {anchor.qc_status === 'MANUAL_REVIEW' && <AlertTriangle className="w-3 h-3" />}
                              {anchor.qc_status === 'PASS' ? 'QC Pass' : 
                               anchor.qc_status === 'FAIL' ? 'QC Fail' : 
                               anchor.qc_status === 'MANUAL_REVIEW' ? 'Review' : 'Pending'}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="font-mono text-xs text-muted-foreground">Scene {anchor.scene}</p>
                      <div className="flex items-center gap-2">
                        {anchor.generated_image_url && (
                          <>
                            <a
                              href={`${API}/files/${anchor.generated_image_url.split('/').pop()}`}
                              download={`anchor_scene_${anchor.scene}.png`}
                              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
                              data-testid={`download-image-${i}-btn`}
                            >
                              <Download className="w-3 h-3" />
                            </a>
                            <button
                              onClick={() => openImageEditDialog(i)}
                              className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
                              data-testid={`edit-image-${i}-btn`}
                            >
                              <Pencil className="w-3 h-3" />
                              Edit
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Fullscreen Image Overlay */}
              {fullscreenImageIndex !== null && project?.anchor_images?.[fullscreenImageIndex]?.generated_image_url && (
                <div 
                  className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center cursor-pointer"
                  onClick={() => setFullscreenImageIndex(null)}
                  onKeyDown={(e) => e.key === 'Escape' && setFullscreenImageIndex(null)}
                  tabIndex={0}
                  autoFocus
                  data-testid="fullscreen-image-overlay"
                >
                  <div className="relative max-w-[90vw] max-h-[90vh]">
                    <img
                      src={`${API}/files/${project.anchor_images[fullscreenImageIndex].generated_image_url.split('/').pop()}?v=${imageVersions[fullscreenImageIndex] || Date.now()}`}
                      alt={`Scene ${project.anchor_images[fullscreenImageIndex].scene} - Full`}
                      className="max-w-full max-h-[90vh] object-contain rounded-lg"
                      data-testid="fullscreen-image"
                    />
                    <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">
                      Scene {project.anchor_images[fullscreenImageIndex].scene} — click anywhere to close
                    </p>
                  </div>
                </div>
              )}

              <div className="flex justify-center">
                <Button
                  onClick={generateVideos}
                  disabled={processing}
                  className="px-12 h-14 rounded-full bg-primary hover:opacity-90 text-lg font-medium gap-2"
                  data-testid="generate-videos-btn"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Generating Videos...
                    </>
                  ) : (
                    <>
                      Generate Videos
                      <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 7: Video Scenes (V1/V2/10s only - V3 is automated) */}
          {(currentStep === 7 && (videoType === "20_second_ad" || videoType === "20_second_ad_v2" || videoType === "10_second_ad" || videoType === "24_second_ad_premium")) && (
            <motion.div
              key="step6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Video className="w-8 h-8 text-primary" strokeWidth={1.5} />
                </div>
                <h2 className="font-display text-2xl font-semibold mb-2">Video Scenes</h2>
                <p className="text-muted-foreground">
                  {videoType === "10_second_ad" ? "2 × 5-second video clips" : videoType === "24_second_ad_premium" ? "4 × 6-second video clips" : "4 × 5-second video clips"}
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
                {project?.video_scenes?.map((scene, i) => (
                  <div key={i} className="space-y-2">
                    <div className={`relative video-preview rounded-xl overflow-hidden bg-secondary ${aspectRatio === '16:9' ? 'landscape' : ''}`}>
                      {scene.video_url ? (
                        <>
                          <video 
                            key={`video-${i}-${videoVersions[i] || 0}`}
                            src={`${API}/files/${scene.video_url.split('/').pop()}?v=${videoVersions[i] || Date.now()}`}
                            className="w-full h-full object-cover"
                            controls
                            data-testid={`video-scene-${i}`}
                          />
                        </>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="font-mono text-xs text-muted-foreground">Scene {scene.scene} · {scene.duration}</p>
                      {scene.video_url && (
                        <button
                          onClick={() => openEditDialog(i)}
                          className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
                          data-testid={`edit-scene-${i}-btn`}
                        >
                          <Pencil className="w-3 h-3" />
                          Edit
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-center">
                <Button
                  onClick={() => setCurrentStep(8)}
                  className="px-12 h-14 rounded-full bg-primary hover:opacity-90 text-lg font-medium gap-2"
                  data-testid="continue-to-audio-btn"
                >
                  Continue to Audio
                  <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 8: Audio Selection (V1/V2/10s) OR Step 3: Audio Selection (V3/V5) */}
          {((currentStep === 8 && (videoType === "20_second_ad" || videoType === "20_second_ad_v2" || videoType === "10_second_ad" || videoType === "24_second_ad_premium")) ||
            (currentStep === 3 && (videoType === "20_second_ad_v3" || videoType === "10_second_ad_v5"))) && (
            <motion.div
              key="step8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Mic className="w-8 h-8 text-primary" strokeWidth={1.5} />
                </div>
                <h2 className="font-display text-2xl font-semibold mb-2">Audio</h2>
                <p className="text-muted-foreground">Choose voiceover narration or background music</p>
              </div>

              {/* Audio Type Toggle */}
              <div className="max-w-xl mx-auto">
                <div className="bg-secondary/50 rounded-xl p-6 space-y-4">
                  <h3 className="font-display font-semibold text-center">Select Audio Type</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div
                      onClick={() => setAudioType("voiceover")}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        audioType === "voiceover" 
                          ? "border-primary bg-primary/5" 
                          : "border-border hover:border-primary/50"
                      }`}
                      data-testid="audio-type-voiceover"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${audioType === "voiceover" ? "border-primary bg-primary" : "border-muted-foreground"}`}>
                          {audioType === "voiceover" && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <Mic className="w-4 h-4" />
                        <span className="font-semibold">Voiceover</span>
                      </div>
                      <p className="text-xs text-muted-foreground">AI voice narration for each scene</p>
                    </div>
                    <div
                      onClick={() => setAudioType("music")}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        audioType === "music" 
                          ? "border-primary bg-primary/5" 
                          : "border-border hover:border-primary/50"
                      }`}
                      data-testid="audio-type-music"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${audioType === "music" ? "border-primary bg-primary" : "border-muted-foreground"}`}>
                          {audioType === "music" && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <Play className="w-4 h-4" />
                        <span className="font-semibold">Music</span>
                      </div>
                      <p className="text-xs text-muted-foreground">AI-generated background music</p>
                    </div>
                  </div>

                  {/* Voiceover Options */}
                  {audioType === "voiceover" && (
                    <div className="space-y-3 pt-4 border-t border-border">
                      <label className="text-sm font-medium">Select Voice</label>
                      <Select value={selectedVoice} onValueChange={setSelectedVoice}>
                        <SelectTrigger className="h-12 rounded-lg">
                          <SelectValue placeholder="Select a voice" />
                        </SelectTrigger>
                        <SelectContent>
                          {voices.length > 0 ? (
                            voices.map((voice) => (
                              <SelectItem key={voice.voice_id} value={voice.voice_id}>
                                {voice.name}
                              </SelectItem>
                            ))
                          ) : (
                            <>
                              <SelectItem value="21m00Tcm4TlvDq8ikWAM">Rachel (Default)</SelectItem>
                              <SelectItem value="EXAVITQu4vr4xnSDxMaL">Bella</SelectItem>
                              <SelectItem value="ErXwobaYiN019PkySvjV">Antoni</SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                      
                      {/* Script Preview for Voiceover */}
                      <div className="bg-white rounded-lg p-4 space-y-3 mt-4">
                        <h4 className="font-medium text-sm">Script Preview</h4>
                        {project?.script && (videoType === "10_second_ad" ? ['scene_1', 'scene_2'] : ['scene_1', 'scene_2', 'scene_3', 'scene_4']).map((sceneKey, i) => {
                          const scene = project.script[sceneKey];
                          if (!scene) return null;
                          const hasVoiceover = project?.voiceover_paths?.[i];
                          return (
                            <div key={sceneKey} className="space-y-1">
                              <div className="flex items-start gap-2 text-sm">
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${hasVoiceover ? 'bg-green-100' : 'bg-muted'}`}>
                                  {hasVoiceover ? (
                                    <Check className="w-3 h-3 text-green-600" strokeWidth={2} />
                                  ) : (
                                    <span className="text-xs font-mono">{i + 1}</span>
                                  )}
                                </div>
                                <p className="text-muted-foreground">{scene?.voiceover}</p>
                              </div>
                              {hasVoiceover && (
                                <audio
                                  key={`voiceover-${i}-${voiceoverVersion}`}
                                  src={`${API}/files/${hasVoiceover.split('/').pop()}?v=${voiceoverVersion || Date.now()}`}
                                  controls
                                  className="w-full h-8"
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Music Options */}
                  {audioType === "music" && (
                    <div className="space-y-3 pt-4 border-t border-border">
                      <label className="text-sm font-medium">Select Mood</label>
                      <Select value={selectedMood} onValueChange={setSelectedMood}>
                        <SelectTrigger className="h-12 rounded-lg">
                          <SelectValue placeholder="Select a mood" />
                        </SelectTrigger>
                        <SelectContent>
                          {musicMoods.length > 0 ? (
                            musicMoods.map((mood) => (
                              <SelectItem key={mood.id} value={mood.id}>
                                {mood.name}
                              </SelectItem>
                            ))
                          ) : (
                            <>
                              <SelectItem value="upbeat">Upbeat / Energetic</SelectItem>
                              <SelectItem value="calm">Calm / Relaxing</SelectItem>
                              <SelectItem value="premium">Premium / Luxury</SelectItem>
                              <SelectItem value="fun">Fun / Playful</SelectItem>
                              <SelectItem value="urgent">Urgent / Exciting</SelectItem>
                              <SelectItem value="dramatic">Dramatic / Cinematic</SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                      
                      {/* Music Preview if already generated */}
                      {project?.music_path && (
                        <div className="bg-white rounded-lg p-4 space-y-3 mt-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                              <Check className="w-5 h-5 text-green-600" strokeWidth={2} />
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium">
                                {musicMoods.find(m => m.id === (project?.music_mood || selectedMood))?.name || "Background Music"}
                              </p>
                              <p className="text-xs text-muted-foreground">20-second track</p>
                            </div>
                          </div>
                          <audio 
                            key={`music-${musicVersion}`}
                            src={`${API}/files/${project.music_path.split('/').pop()}?v=${musicVersion || Date.now()}`}
                            controls
                            className="w-full"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button
                  onClick={audioType === "voiceover" ? generateVoiceovers : generateMusic}
                  disabled={processing}
                  className="px-12 h-14 rounded-full bg-primary hover:opacity-90 text-lg font-medium gap-2"
                  data-testid="generate-audio-btn"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {audioType === "voiceover" ? "Generating Voiceovers..." : "Generating Music..."}
                    </>
                  ) : (
                    <>
                      {audioType === "voiceover" ? (project?.voiceover_paths?.length ? "Regenerate Voiceovers" : "Generate Voiceovers") : (project?.music_path ? "Regenerate Music" : "Generate Music")}
                      {audioType === "voiceover" ? <CreditBadge actionKey="voiceover_generation" className="ml-2 text-amber-200" /> : <CreditBadge actionKey="music_generation" className="ml-2 text-amber-200" />}
                      <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
                    </>
                  )}
                </Button>
                
                {/* Show Create Final Video button only if audio is generated */}
                {((audioType === "voiceover" && project?.voiceover_paths?.length > 0) || 
                  (audioType === "music" && project?.music_path)) && (
                  <Button
                    onClick={stitchVideo}
                    disabled={processing}
                    variant="outline"
                    className="px-12 h-14 rounded-full font-medium gap-2"
                    data-testid="stitch-video-btn"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Creating Final Video...
                      </>
                    ) : (
                      <>
                        Create Final Video
                        <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
                      </>
                    )}
                  </Button>
                )}
              </div>
            </motion.div>
          )}

          {/* Step 9: Final Video (V1/V2/10s) OR Step 4: Final Video (V3/V5) */}
          {((currentStep === 9 && (videoType === "20_second_ad" || videoType === "20_second_ad_v2" || videoType === "10_second_ad" || videoType === "24_second_ad_premium")) ||
            (currentStep === 4 && (videoType === "20_second_ad_v3" || videoType === "10_second_ad_v5"))) && (
            <motion.div
              key="step8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Check className="w-8 h-8 text-green-600" strokeWidth={2} />
                </div>
                <h2 className="font-display text-2xl font-semibold mb-2">Your Ad is Ready!</h2>
                <p className="text-muted-foreground">
                  {videoType === "10_second_ad_v5" || videoType === "10_second_ad" ? "10-second" : videoType === "24_second_ad_premium" ? "24-second premium" : "20-second"} video ad for {project?.product_analysis?.product_name}
                </p>
              </div>

              <div className="max-w-md mx-auto">
                <div className={`video-preview rounded-2xl overflow-hidden bg-black shadow-float ${aspectRatio === '16:9' ? 'landscape' : ''}`}>
                  {project?.final_video_url ? (
                    <video 
                      src={`${API}/files/${project.final_video_url.split('/').pop()}`}
                      className="w-full h-full"
                      controls
                      autoPlay
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center min-h-[400px]">
                      <p className="text-white/50">Video not available</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                {project?.final_video_url && (
                  <Button
                    className="px-8 h-12 rounded-full bg-primary hover:opacity-90 font-medium gap-2"
                    data-testid="download-video-btn"
                    onClick={() => {
                      const a = document.createElement("a");
                      a.href = `${API}/download-video?url=${encodeURIComponent(project.final_video_url)}`;
                      a.download = project.final_video_url.split("/").pop()?.split("?")[0] || "video.mp4";
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                    }}
                  >
                    <Download className="w-4 h-4" strokeWidth={1.5} />
                    Download Video
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => navigate('/projects')}
                  className="px-8 h-12 rounded-full font-medium"
                  data-testid="back-to-projects-btn"
                >
                  Back to Projects
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Video Scene Edit Dialog */}
      <Dialog open={editingSceneIndex !== null} onOpenChange={(open) => !open && closeEditDialog()}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              Edit Scene {editingSceneIndex !== null ? editingSceneIndex + 1 : ""} Prompt
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Video Motion Prompt</label>
              <Textarea
                value={editPrompt}
                onChange={(e) => setEditPrompt(e.target.value)}
                placeholder="Describe the motion and action for this scene..."
                className="min-h-[120px] resize-none"
                data-testid="edit-prompt-textarea"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Describe the camera movement, subject actions, and visual transitions you want in this 5-second clip.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeEditDialog}
              disabled={regeneratingScene}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={regenerateVideoScene}
              disabled={regeneratingScene || !editPrompt.trim()}
              className="rounded-full bg-primary hover:opacity-90"
              data-testid="regenerate-video-btn"
            >
              {regeneratingScene ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Regenerating...
                </>
              ) : (
                <>
                  Regenerate Video
                  <CreditBadge actionKey="video_edit" className="ml-2" />
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Anchor Image Edit Dialog */}
      <Dialog open={editingImageIndex !== null} onOpenChange={(open) => !open && closeImageEditDialog()}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              Edit Scene {editingImageIndex !== null ? editingImageIndex + 1 : ""} Image
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Edit Instructions</label>
              <Textarea
                value={imageEditPrompt}
                onChange={(e) => setImageEditPrompt(e.target.value)}
                placeholder="Describe the changes you want to make to this image..."
                className="min-h-[120px] resize-none"
                data-testid="edit-image-prompt-textarea"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Describe how you want to modify this image. For example: "change the background to a beach", "make the lighting warmer", "add sunglasses to the person".
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeImageEditDialog}
              disabled={regeneratingImage}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={regenerateAnchorImage}
              disabled={regeneratingImage || !imageEditPrompt.trim()}
              className="rounded-full bg-primary hover:opacity-90"
              data-testid="regenerate-image-btn"
            >
              {regeneratingImage ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Editing Image...
                </>
              ) : (
                <>
                  Apply Changes
                  <CreditBadge actionKey="image_edit" className="ml-2" />
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QC Failed Dialog */}
      <Dialog open={showQcDialog} onOpenChange={setShowQcDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              {qcType === "video" ? "Video" : "Image"} Quality Check Results
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Some {qcType === "video" ? "video scenes" : "anchor images"} didn't pass quality control. Would you like to regenerate them?
            </p>
            
            <div className="space-y-3 max-h-60 overflow-y-auto">
              {qcFailedScenes.map((scene, idx) => (
                <div key={idx} className="p-3 rounded-lg bg-secondary/50 border border-border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{qcType === "video" ? "Video" : "Image"} Scene {scene.scene}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      scene.status === 'MANUAL_REVIEW' 
                        ? 'bg-yellow-500/20 text-yellow-600' 
                        : 'bg-red-500/20 text-red-600'
                    }`}>
                      {scene.status === 'MANUAL_REVIEW' ? 'Manual Review' : `Score: ${scene.score}%`}
                    </span>
                  </div>
                  {scene.issues?.length > 0 && (
                    <ul className="text-xs text-muted-foreground space-y-1">
                      {scene.issues.map((issue, i) => (
                        <li key={i} className="flex items-start gap-1">
                          <span className="text-red-400">•</span>
                          {issue}
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-2 flex justify-end">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => qcType === "video" ? retryVideoQcForScene(scene.scene - 1) : retryQcForScene(scene.scene - 1)}
                      disabled={retryingQc || scene.status === 'MANUAL_REVIEW'}
                      className="text-xs h-7"
                    >
                      {retryingQc ? (
                        <Loader2 className="w-3 h-3 animate-spin mr-1" />
                      ) : (
                        <RefreshCw className="w-3 h-3 mr-1" />
                      )}
                      {scene.status === 'MANUAL_REVIEW' ? 'Max Retries' : 'Retry'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowQcDialog(false)}>
              Continue Anyway
            </Button>
            <Button 
              onClick={() => {
                qcFailedScenes.forEach(scene => {
                  if (scene.status !== 'MANUAL_REVIEW') {
                    qcType === "video" ? retryVideoQcForScene(scene.scene - 1) : retryQcForScene(scene.scene - 1);
                  }
                });
              }}
              disabled={retryingQc || qcFailedScenes.every(s => s.status === 'MANUAL_REVIEW')}
            >
              Retry All Failed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectWizard;
